import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

export type Language = "en" | "fr" | "ar";

export interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  dir: "ltr" | "rtl";
  isRTL: boolean;
}

// Comprehensive translations for the entire platform
const translations: Record<Language, Record<string, string>> = {
  en: {
    // ==================== NAVIGATION ====================
    "nav.about": "What is SYNRA",
    "nav.services": "Services",
    "nav.training": "Training",
    "nav.prompts": "Prompts",
    "nav.partners": "Partners",
    "nav.signin": "Sign In",
    "nav.getstarted": "Get Started",
    
    // ==================== LANGUAGE SWITCHER ====================
    "lang.change": "Change language",
    "lang.switchTo": "Switch to",
    "lang.en": "English",
    "lang.fr": "French",
    "lang.ar": "Arabic",
    
    // ==================== COMMON ====================
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.success": "Success",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.submit": "Submit",
    "common.close": "Close",
    "common.back": "Back",
    "common.next": "Next",
    "common.continue": "Continue",
    "common.learnMore": "Learn more",
    "common.viewAll": "View All",
    "common.comingSoon": "Coming Soon",
    "common.backToHome": "Back to home",
    "common.returnToHome": "Return to Home",
    "common.questions": "Questions?",
    "common.contactUs": "Contact us at",

    // ==================== HERO SECTION ====================
    "hero.badge": "AI & Automation Platform",
    "hero.title": "Transform Your Business with",
    "hero.titleHighlight": "Practical AI",
    "hero.subtitle": "SYNRA empowers Moroccan companies and youth to master automation and artificial intelligence — structured, secure, and results-driven.",
    "hero.cta.partner": "Join as Partner",
    "hero.cta.youth": "Youth Program",
    "hero.stat.partners": "Partner Companies",
    "hero.stat.automations": "Automations Deployed",
    "hero.stat.youth": "Youth Trained",

    // ==================== ABOUT SECTION ====================
    "about.label": "About SYNRA",
    "about.title": "AI & Automation,",
    "about.titleHighlight": "Made Practical",
    "about.description1": "SYNRA is more than a software platform — it's an AI ecosystem designed for the Moroccan market. We connect businesses seeking digital transformation with qualified partners and trained talent.",
    "about.description2": "Our mission is to make AI and automation accessible, understandable, and actionable. Whether you're a company looking to optimize operations, a consultant expanding your services, or a young professional building future-ready skills — SYNRA is your platform.",
    "about.value.trust.title": "Trust & Security",
    "about.value.trust.desc": "Enterprise-grade security with transparent processes. Your data and operations are protected.",
    "about.value.results.title": "Practical Results",
    "about.value.results.desc": "No hype, just measurable outcomes. We focus on implementations that deliver real ROI.",
    "about.value.knowledge.title": "Knowledge Transfer",
    "about.value.knowledge.desc": "We don't just build — we teach. Your team gains the skills to maintain and extend solutions.",
    "about.value.ecosystem.title": "Ecosystem Approach",
    "about.value.ecosystem.desc": "Connecting companies, partners, and talent in a unified platform for sustainable growth.",

    // ==================== SERVICES SECTION ====================
    "services.label": "Our Services",
    "services.title": "Practical AI Solutions for",
    "services.titleHighlight": "Real Business Needs",
    "services.subtitle": "We deliver automation and AI solutions that solve actual problems — no buzzwords, just results.",
    "services.workflow.title": "Workflow Automation",
    "services.workflow.desc": "Eliminate repetitive tasks and streamline operations with custom automation solutions. From simple workflows to complex integrations.",
    "services.workflow.f1": "Process Mapping",
    "services.workflow.f2": "n8n & Zapier Integration",
    "services.workflow.f3": "API Connections",
    "services.workflow.f4": "Custom Triggers",
    "services.ai.title": "AI Agents",
    "services.ai.desc": "Deploy intelligent agents that understand your business context. Customer support, data analysis, and decision assistance.",
    "services.ai.f1": "Custom AI Assistants",
    "services.ai.f2": "NLP Integration",
    "services.ai.f3": "Multi-channel Support",
    "services.ai.f4": "Continuous Learning",
    "services.optimization.title": "System Optimization",
    "services.optimization.desc": "Audit, analyze, and optimize your existing tech stack. Identify bottlenecks and implement improvements.",
    "services.optimization.f1": "Tech Stack Audit",
    "services.optimization.f2": "Performance Analysis",
    "services.optimization.f3": "Integration Review",
    "services.optimization.f4": "Scalability Planning",

    // ==================== TRAINING SECTION ====================
    "training.label": "Youth Training Program",
    "training.title": "Build Real Skills,",
    "training.titleHighlight": "Not Just Theory",
    "training.subtitle": "Our training program is designed to create job-ready professionals. Learn by doing — with mentorship, projects, and clear career paths.",
    "training.path.title": "Learning Path",
    "training.level.beginner": "Beginner",
    "training.level.intermediate": "Intermediate",
    "training.level.advanced": "Advanced",
    "training.level.expert": "Expert",
    "training.course.fundamentals": "Automation Fundamentals",
    "training.course.n8n": "n8n & Advanced Workflows",
    "training.course.agents": "AI Agents Development",
    "training.course.projects": "Real-World Projects",
    "training.duration.weeks": "weeks",
    "training.duration.ongoing": "Ongoing",
    "training.skill.workflow": "Workflow Logic",
    "training.skill.integrations": "Basic Integrations",
    "training.skill.nocode": "No-Code Tools",
    "training.skill.n8n": "n8n Mastery",
    "training.skill.api": "API Integration",
    "training.skill.errors": "Error Handling",
    "training.skill.llm": "LLM Integration",
    "training.skill.agents": "Custom Agents",
    "training.skill.deployment": "Production Deployment",
    "training.skill.client": "Client Projects",
    "training.skill.team": "Team Collaboration",
    "training.skill.industry": "Industry Solutions",
    "training.benefit.curriculum.title": "Structured Curriculum",
    "training.benefit.curriculum.desc": "Clear progression from basics to advanced concepts",
    "training.benefit.projects.title": "Real Projects",
    "training.benefit.projects.desc": "Work on actual company projects, not just exercises",
    "training.benefit.certification.title": "Skill Certification",
    "training.benefit.certification.desc": "Earn recognized credentials as you progress",
    "training.cta.title": "Ready to Start Your Journey?",
    "training.cta.desc": "Join our youth program and gain the skills that companies are actively hiring for. Limited spots available each cohort.",
    "training.cta.button": "Apply Now",

    // ==================== PARTNERS SECTION ====================
    "partners.label": "Partner Program",
    "partners.title": "Grow Your Business with",
    "partners.titleHighlight": "SYNRA",
    "partners.subtitle": "Join our partner network and offer enterprise-grade AI & automation services to your clients. We provide the technology, you provide the relationship.",
    "partners.ideal.title": "Ideal Partners",
    "partners.type.agencies": "Agencies",
    "partners.type.agencies.desc": "Digital marketing and web agencies expanding into automation",
    "partners.type.consultants": "Consultants",
    "partners.type.consultants.desc": "Business consultants adding AI to their service offerings",
    "partners.type.it": "IT Firms",
    "partners.type.it.desc": "Technology companies seeking proven automation solutions",
    "partners.cta": "Become a Partner",
    "partners.benefit.whitelabel.title": "White-Label Solutions",
    "partners.benefit.whitelabel.desc": "Sell SYNRA services under your brand. Full partner portal to manage clients.",
    "partners.benefit.revenue.title": "Revenue Share",
    "partners.benefit.revenue.desc": "Competitive commission structure. Track earnings and payouts in real-time.",
    "partners.benefit.sales.title": "Sales Support",
    "partners.benefit.sales.desc": "Access pitch decks, proposals, and templates. We help you close deals.",
    "partners.benefit.dashboard.title": "Client Dashboard",
    "partners.benefit.dashboard.desc": "Manage your companies, track automation status, and monitor results.",

    // ==================== TESTIMONIALS ====================
    "testimonials.badge": "Success Stories",
    "testimonials.title": "What Our Graduates Say",
    "testimonials.subtitle": "Real results from real people who transformed their careers with SYNRA.",
    "testimonials.1.name": "Yassine El Amrani",
    "testimonials.1.sector": "Digital Marketing — Casablanca",
    "testimonials.1.quote": "Thanks to SYNRA, I automated 80% of my agency's reporting. Our productivity doubled in just 3 months.",
    "testimonials.2.name": "Fatima Zahra Bennani",
    "testimonials.2.sector": "E-commerce — Rabat",
    "testimonials.2.quote": "The AI training gave me the skills to build chatbots for my online store. Customer satisfaction increased by 45%.",
    "testimonials.3.name": "Omar Tazi",
    "testimonials.3.sector": "Education Technology — Marrakech",
    "testimonials.3.quote": "SYNRA's program helped me transition from traditional teaching to AI-powered education. I now train other educators.",

    // ==================== CTA SECTION ====================
    "cta.badge": "Start Your AI Journey",
    "cta.title": "Ready to Transform Your",
    "cta.titleHighlight": "Business?",
    "cta.subtitle": "Whether you're a company seeking automation, a partner looking to expand, or a young talent building skills — SYNRA is your platform.",
    "cta.partner": "Join as Partner",
    "cta.training": "Apply for Training",

    // ==================== FOOTER ====================
    "footer.tagline": "Empowering Moroccan companies and youth with practical AI & Automation solutions.",
    "footer.platform": "Platform",
    "footer.platform.services": "Services",
    "footer.platform.training": "Training",
    "footer.platform.partners": "Partners",
    "footer.platform.pricing": "Pricing",
    "footer.company": "Company",
    "footer.company.about": "About",
    "footer.company.careers": "Careers",
    "footer.company.blog": "Blog",
    "footer.company.contact": "Contact",
    "footer.legal": "Legal",
    "footer.legal.privacy": "Privacy Policy",
    "footer.legal.terms": "Terms of Service",
    "footer.legal.cookies": "Cookie Policy",
    "footer.copyright": "© {year} SYNRA. All rights reserved.",
    "footer.madeIn": "Made with precision in Morocco",

    // ==================== AUTH PAGE ====================
    "auth.welcome.title": "Welcome to Your",
    "auth.welcome.highlight": "AI Journey",
    "auth.welcome.subtitle": "Join Morocco's leading AI & Automation ecosystem. Whether you're a business partner or an aspiring talent, SYNRA is your platform for growth.",
    "auth.card.partners.title": "Partners",
    "auth.card.partners.desc": "Expand your services with AI & Automation solutions",
    "auth.card.youth.title": "Youth",
    "auth.card.youth.desc": "Build real skills through hands-on training",
    "auth.tab.signin": "Sign In",
    "auth.tab.partner": "Partner",
    "auth.tab.youth": "Youth",
    "auth.signin.title": "Welcome Back",
    "auth.signin.subtitle": "Sign in with your phone number and PIN",
    "auth.partner.title": "Become a Partner",
    "auth.partner.subtitle": "Join our network and offer AI services to your clients",
    "auth.youth.title": "Join Training Program",
    "auth.youth.subtitle": "Start your journey to becoming an AI professional",
    "auth.backToHome": "Back to home",

    // ==================== 404 PAGE ====================
    "notfound.title": "404",
    "notfound.message": "Oops! Page not found",
    "notfound.link": "Return to Home",

    // ==================== YOUTH SIDEBAR ====================
    "youth.nav.main": "Main",
    "youth.nav.learning": "Learning",
    "youth.nav.progress": "Progress & Achievements",
    "youth.nav.resources": "Resources & Growth",
    "youth.home": "Home",
    "youth.classroom": "Classroom",
    "youth.lessons": "Lessons",
    "youth.paths": "Learning Paths",
    "youth.quiz": "Quiz",
    "youth.progress": "Progress",
    "youth.certificates": "Certificates",
    "youth.skills": "Skills",
    "youth.resources": "Resources",
    "youth.practicePosts": "Practice Posts",
    "youth.projects": "Projects",
    "youth.opportunities": "Opportunities",
    "youth.meet": "Meet",
    "youth.chat": "Community Chat",
     "youth.tasks": "Tasks",
     "youth.signout": "Sign Out",

    // ==================== YOUTH HOME ====================
    "youth.home.title": "Welcome",
    "youth.home.subtitle": "Your AI Training Journey Starts Here",
    "youth.home.platform": "SYNRA Platform",
    "youth.home.greeting": "Welcome back, {name}!",
    "youth.home.description": "Master AI and automation skills through structured, professional training. Build real-world expertise and unlock new opportunities.",
    "youth.home.cta.classroom": "Go to Classroom",
    "youth.home.cta.paths": "View Learning Paths",
    "youth.home.card.learning.title": "Structured Learning",
    "youth.home.card.learning.desc": "Progress through carefully designed levels, from beginner to advanced, with video lessons and hands-on practice.",
    "youth.home.card.projects.title": "Real-World Projects",
    "youth.home.card.projects.desc": "Apply your knowledge to actual projects and build a portfolio that showcases your AI and automation skills.",
    "youth.home.card.career.title": "Career Opportunities",
    "youth.home.card.career.desc": "Earn certificates, unlock opportunities, and connect with companies looking for skilled AI professionals.",
    "youth.home.coming.label": "Coming Soon",
    "youth.home.coming.title": "New Features & Content",
    "youth.home.coming.desc": "We're constantly adding new training modules, opportunities, and features to help you advance your AI journey. Stay tuned!",

    // ==================== YOUTH CLASSROOM ====================
    "youth.classroom.title": "Classroom",
    "youth.classroom.subtitle": "Your enrolled programs and learning progress",
    "youth.classroom.loading": "Loading your enrolled programs...",
    "youth.classroom.stats.total": "Total Levels",
    "youth.classroom.stats.inProgress": "In Progress",
    "youth.classroom.stats.completed": "Completed",
    "youth.classroom.empty.title": "No programs available yet",
    "youth.classroom.empty.desc": "Training content is being prepared. Check back soon to start your learning journey.",
    "youth.classroom.levels.title": "Your Learning Levels",
    "youth.classroom.viewPaths": "View All Paths",
    "youth.classroom.lessons": "lessons",
    "youth.classroom.min": "min",
    "youth.classroom.complete": "complete",
    "youth.classroom.back": "Back to Levels",
    "youth.classroom.yourProgress": "Your Progress",
    "youth.classroom.contentPreparing": "Content is being prepared",
    "youth.classroom.checkBack": "Check back soon.",
    "youth.classroom.lessonsTitle": "Lessons",
    "youth.classroom.allComplete.title": "All lessons completed!",
    "youth.classroom.allComplete.desc": "Ready to test your knowledge?",
    "youth.classroom.takeQuiz": "Take Quiz",
    "youth.classroom.selectLesson": "Select a lesson to watch",
    "youth.classroom.chooseLesson": "Choose a lesson from the list to start learning",
    "youth.classroom.markComplete": "Mark as Complete",
    "youth.classroom.completed": "Completed",
    "youth.classroom.watchToComplete": "Watch the video to complete",
    "youth.classroom.replay": "Replay",
    "youth.classroom.requiredWatch": "Required watch time",
    "youth.classroom.seekBlocked": "Forward seeking is not allowed",
    "youth.classroom.noSkip": "No skip",
    "youth.classroom.watchedTime": "Total watched",
    "youth.classroom.unlocked": "Unlocked",
    "youth.classroom.remaining": "remaining",
    
    // ==================== SMART CONTEXT PANEL ====================
    "youth.classroom.contextPanel.title": "Learning Context",
    "youth.classroom.contextPanel.currentContext": "Current Context",
    "youth.classroom.contextPanel.upcoming": "Coming Up",
    "youth.classroom.contextPanel.previous": "Previous",
    "youth.classroom.contextPanel.openLink": "Open Link",
    "youth.classroom.contextPanel.waitingForContext": "Context will appear as you watch",
    "youth.classroom.contextPanel.types.platform": "Platform",
    "youth.classroom.contextPanel.types.tool": "Tool",
    "youth.classroom.contextPanel.types.documentation": "Documentation",
    "youth.classroom.contextPanel.types.note": "Note",
    "youth.classroom.contextPanel.types.exercise": "Exercise",

    // ==================== DIFFICULTY LEVELS ====================
    "difficulty.beginner": "Beginner",
    "difficulty.intermediate": "Intermediate",
    "difficulty.advanced": "Advanced",

    // ==================== EMPTY STATES ====================
    "empty.noData": "No data available",
    "empty.noLessons": "No lessons available",
    "empty.noCertificates": "No certificates earned yet",
    "empty.noOpportunities": "No opportunities available",
    "empty.noProjects": "No projects assigned",
    "empty.noTasks": "No tasks assigned",
    "empty.noMeets": "No scheduled meetings",
    "empty.noSkills": "No skills recorded yet",
    "empty.noResources": "No resources available",

    // ==================== ERRORS ====================
    "error.generic": "Something went wrong",
    "error.notFound": "Page not found",
    "error.unauthorized": "Unauthorized access",
    "error.network": "Network error. Please try again.",
    "error.loadFailed": "Failed to load data",
    "error.validation": "Validation Error",
    "error.loginFailed": "Login Failed",
    "error.invalidCredentials": "Invalid phone number or PIN. Please try again.",
    "error.pendingConfirmation": "Your account is pending confirmation.",
    "error.registrationFailed": "Registration Failed",
    "error.alreadyRegistered": "This phone number is already registered. Please sign in instead.",
    "error.profileCreationFailed": "Could not create profile. Please try again.",
    "error.userCreationFailed": "Could not create user account.",
    "error.unexpected": "An unexpected error occurred. Please try again.",

    // ==================== AUTH FORMS ====================
    "auth.form.phone": "Phone Number",
    "auth.form.phone.placeholder": "+212 6XX XXX XXX",
    "auth.form.pin": "PIN (4-6 digits)",
    "auth.form.pin.placeholder": "••••••",
    "auth.form.fullName": "Full Name",
    "auth.form.fullName.placeholder.partner": "Ahmed El Mansouri",
    "auth.form.fullName.placeholder.youth": "Youssef Benjelloun",
    "auth.form.companyName": "Company Name",
    "auth.form.companyName.placeholder": "Your Company SARL",
    "auth.form.domain": "Business Domain",
    "auth.form.domain.placeholder": "e.g., Marketing, IT Services",
    "auth.form.email": "Email (Optional)",
    "auth.form.email.placeholder": "ahmed@company.ma",
    "auth.form.city": "City",
    "auth.form.city.placeholder": "Casablanca",
    "auth.form.level": "Current Level",
    "auth.form.level.beginner": "Beginner - No experience",
    "auth.form.level.intermediate": "Intermediate - Some knowledge",
    "auth.form.level.advanced": "Advanced - Experienced",
    "auth.form.required": "*",
    "auth.form.signin": "Sign In",
    "auth.form.signingIn": "Signing in...",
    "auth.form.createAccount": "Create Account",
    "auth.form.creatingAccount": "Creating Account...",
    "auth.form.welcomeBack": "Welcome back!",
    "auth.form.loginSuccess": "Login successful.",

    // ==================== PASSWORD CONFIRMATION ====================
    "auth.password.title": "Your Access PIN",
    "auth.password.subtitle": "Save this PIN securely. You will need it to sign in.",
    "auth.password.warning": "This PIN will NOT be shown again!",
    "auth.password.copied": "PIN copied to clipboard!",
    "auth.password.copy": "Copy PIN",
    "auth.password.confirm": "I have saved my PIN",

    // ==================== PENDING APPROVAL ====================
    "auth.pending.title": "Application Submitted",
    "auth.pending.greeting": "Thank you, {name}!",
    "auth.pending.message": "Your application is being reviewed by our team. You will receive a notification once your account is approved.",
    "auth.pending.signin": "Go to Sign In",

    // ==================== YOUTH LEARNING PATHS ====================
    "youth.paths.title": "Learning Paths",
    "youth.paths.subtitle": "Structured training programs to build your AI and automation expertise",
    "youth.paths.empty.title": "No learning paths available",
    "youth.paths.empty.desc": "Training programs are being prepared by the admin. Please check back later.",
    "youth.paths.continue": "Continue Learning",
    "youth.paths.available": "Available Paths",
    "youth.paths.completed": "Completed",
    "youth.paths.locked": "Locked",
    "youth.paths.inProgress": "In Progress",
    "youth.paths.modules": "modules",
    "youth.paths.startPath": "Start Path",
    "youth.paths.review": "Review",
    "youth.paths.unlockMessage": "Complete previous paths to unlock",

    // ==================== YOUTH OPPORTUNITIES ====================
    "youth.opportunities.title": "Opportunities",
    "youth.opportunities.subtitle": "Jobs, internships, and freelance work matched to your skills",
    "youth.opportunities.empty.title": "No opportunities available",
    "youth.opportunities.empty.desc": "New opportunities will appear here as they become available.",
    "youth.opportunities.available": "Available for You",
    "youth.opportunities.locked": "Complete Required Levels",
    "youth.opportunities.apply": "Apply Now",
    "youth.opportunities.remote": "Remote",
    "youth.opportunities.requiredLevels": "Required Levels:",
    "youth.opportunities.type.job": "Full-time Job",
    "youth.opportunities.type.freelance": "Freelance",
    "youth.opportunities.type.internship": "Internship",
    "youth.opportunities.type.contract": "Contract",

    // ==================== YOUTH PROJECTS ====================
    "youth.projects.title": "Projects",
    "youth.projects.subtitle": "Real-world and simulation projects to build your portfolio",
    "youth.projects.empty.title": "No projects assigned",
    "youth.projects.empty.desc": "Projects will appear here when assigned by an admin.",
    "youth.projects.active": "Active Projects",
    "youth.projects.completed": "Completed Projects",
    "youth.projects.role": "Role:",
    "youth.projects.started": "Started:",
    "youth.projects.status.active": "Active",
    "youth.projects.status.completed": "Completed",
    "youth.projects.type.internal": "Internal",
    "youth.projects.type.company": "Company",
    "youth.projects.type.simulation": "Simulation",
    "youth.projects.view": "View Project",
    "youth.projects.progress": "Progress",
    "youth.projects.milestones": "Project Milestones",
    "youth.projects.resources": "Project Resources",
    "youth.projects.noMilestones": "No milestones defined for this project",
    "youth.projects.noResources": "No resources available",
    "youth.projects.submit": "Submit Your Work",
    "youth.projects.pending": "Your submission is under review",
    "youth.projects.instructions": "Submission Instructions",
    "youth.projects.notes": "Work Description",
    "youth.projects.notesPlaceholder": "Describe your work...",
    "youth.projects.url": "Project Link",
    "youth.projects.submitBtn": "Submit Project",
    "youth.projects.submitting": "Submitting...",
    "youth.projects.submissions": "Previous Submissions",
    "youth.projects.feedback": "Feedback",
    "youth.projects.dueDate": "Due Date",
    "youth.projects.deadline": "Deadline",
    "youth.projects.daysRemaining": "days remaining",
    "youth.projects.overdue": "Overdue",
    "youth.projects.dueToday": "Due Today",
    "youth.projects.dueSoon": "Due Soon",
    "youth.projects.urgentWarning": "Only {days} days left!",
    "youth.projects.notFound": "Project not found or not assigned to you",
    "youth.projects.status.pending_review": "Under Review",
    "youth.projects.status.approved": "Approved",
    "youth.projects.status.needs_revision": "Needs Revision",
    "youth.projects.status.rejected": "Rejected",

    // ==================== YOUTH PROGRESS ====================
    "youth.progress.title": "Progress & Evaluation",
    "youth.progress.subtitle": "Track your learning journey and performance metrics",
    "youth.progress.overall": "Overall Progress",
    "youth.progress.avgScore": "Average Score",
    "youth.progress.currentLevel": "Current Level",
    "youth.progress.classroomLevels": "Classroom Levels",
    "youth.progress.learningPaths": "Learning Paths",
    "youth.progress.completed": "Completed",
    "youth.progress.inProgress": "In Progress",
    "youth.progress.available": "Available",
    "youth.progress.totalLevels": "Total Levels",
    "youth.progress.videos": "Videos",
    "youth.progress.quiz": "Quiz",
    "youth.progress.passed": "Passed",
    "youth.progress.notPassed": "Not Passed",
    "youth.progress.taskCompletion": "Task Completion",
    "youth.progress.recentEvaluations": "Recent Evaluations",
    "youth.progress.noEvaluations": "No evaluations yet",
    "youth.progress.of": "of",
    "youth.progress.personalDev": "Personal Development",

    // ==================== YOUTH TASKS ====================
    "youth.tasks.title": "Tasks",
    "youth.tasks.subtitle": "View assigned tasks, submit your work, and track feedback",
    "youth.tasks.empty.title": "No tasks assigned",
    "youth.tasks.empty.desc": "Tasks will appear here when assigned by an admin or mentor.",
    "youth.tasks.empty.caughtUp": "You're all caught up.",
    "youth.tasks.empty.newAssignments": "New assignments will appear here when assigned by your instructor.",
    "youth.tasks.pending": "Pending",
    "youth.tasks.underReview": "Under Review",
    "youth.tasks.completed": "Completed",
    "youth.tasks.submit": "Submit",
    "youth.tasks.resubmit": "Resubmit",
    "youth.tasks.submitTask": "Submit Task",
    "youth.tasks.submitting": "Submitting...",
    "youth.tasks.submissionText": "Submission Text",
    "youth.tasks.link": "Link (optional)",
    "youth.tasks.due": "Due:",
    "youth.tasks.submitted": "Submitted:",
    "youth.tasks.feedback": "Feedback",
    "youth.tasks.describe": "Describe your work or paste your answer...",
    "youth.tasks.overdue": "Overdue",
    "youth.tasks.completion": "Completion",
    "youth.tasks.reliabilityNote": "Impacts index",
    "youth.tasks.reliabilityWarning": "Late submissions may affect your Reliability Index.",
    "youth.tasks.noTasksFilter": "No tasks match this filter.",
    "youth.tasks.filter.all": "All",
    "youth.tasks.filter.pending": "Pending",
    "youth.tasks.filter.submitted": "Submitted",
    "youth.tasks.filter.approved": "Approved",
    "youth.tasks.filter.overdue": "Overdue",
    "youth.tasks.status.approved": "Approved",
    "youth.tasks.status.submitted": "Submitted",
    "youth.tasks.status.needsRevision": "Needs Revision",
    "youth.tasks.status.pending": "Pending",
    "youth.tasks.status.rejected": "Rejected",
    "youth.tasks.filter.active": "Active",
    "youth.tasks.filter.underReview": "Under Review",
    "youth.tasks.filter.needsRevision": "Needs Revision",
    "youth.tasks.filter.completed": "Completed",
    "youth.tasks.empty.levelUnlock": "Complete a level to unlock applied tasks.",

    // ==================== YOUTH PRACTICE POSTS ====================
    "youth.practicePosts.title": "Practice Posts",
    "youth.practicePosts.subtitle": "Interactive learning resources",
    "youth.practicePosts.platformLinks": "Platform Links",
    "youth.practicePosts.steps": "Steps",
    "youth.practicePosts.comments": "Comments",
    "youth.practicePosts.addComment": "Add a comment...",
    "youth.practicePosts.noComments": "No comments yet. Be the first!",
    "youth.practicePosts.noPosts": "No practice posts available yet",

    // ==================== YOUTH CHAT ====================
    "youth.chat.title": "Platform Community",
    "youth.chat.subtitle": "Connect with all platform members",
    "youth.chat.members": "members online",
    "youth.chat.emptyGroup": "No messages yet",
    "youth.chat.emptyGroupHint": "Be the first to send a message in the community!",
    "youth.chat.messagePlaceholder": "Write your message...",

    // ==================== NOTIFICATIONS ====================
    "notifications": "Notifications",
    "markAllRead": "Mark all read",
    "noNotifications": "No notifications yet",

    // ==================== YOUTH COMMENTS ====================
    "youth.comments.title": "Comments",
    "youth.comments.placeholder": "Write your comment or question here...",
    "youth.comments.reply": "Reply",
    "youth.comments.delete": "Delete",
    "youth.comments.replyPlaceholder": "Write your reply...",
    "youth.comments.empty": "No comments yet. Be the first to comment!",
    "youth.comments.error": "An error occurred",
    "youth.comments.deleteError": "Error deleting comment",

    // ==================== YOUTH ROADMAP ====================
    "youth.roadmap.title": "Your Learning Journey",
    "youth.roadmap.subtitle": "Complete all 12 levels to become a certified expert",

    // ==================== YOUTH MEET ====================
    "youth.meet.title": "Meet",
    "youth.meet.subtitle": "Join live sessions with instructors and mentors",
    "youth.meet.empty.title": "No sessions available",
    "youth.meet.empty.desc": "Live sessions will appear here when scheduled for your level.",
    "youth.meet.upcoming": "Upcoming Sessions",
    "youth.meet.past": "Past Sessions",
    "youth.meet.today": "Today",
    "youth.meet.joined": "Joined",
    "youth.meet.attended": "Attended",
    "youth.meet.ended": "Ended",
    "youth.meet.join": "Join Session",
    "youth.meet.rejoin": "Rejoin Session",
    "youth.meet.notAvailable": "Not Available Yet",
    "youth.meet.attendanceRecorded": "Attendance Recorded",
    "youth.meet.attendanceDesc": "Your attendance has been logged for this session.",
    "youth.meet.session": "Session",
    "youth.meet.openNewTab": "This session will open in a new browser tab. Make sure your camera and microphone are ready before joining.",
    "youth.meet.joinLive": "Join Live Session",
    "youth.meet.opensNewTab": "Opens in a new tab",
    "youth.meet.minSession": "min session",

    // ==================== YOUTH LESSONS ====================
    "youth.lessons.title": "Lessons",
    "youth.lessons.subtitle": "Access your learning content, videos, and resources",
    "youth.lessons.empty.title": "No lessons available",
    "youth.lessons.empty.desc": "Start a learning path first to access lessons. Lessons are organized within structured training programs.",
    "youth.lessons.browsePaths": "Browse Learning Paths",
    "youth.lessons.noModules": "No modules yet",
    "youth.lessons.completed": "completed",
    "youth.lessons.selectLesson": "Select a lesson",
    "youth.lessons.chooseLesson": "Choose a lesson from your active paths to view content",
    "youth.lessons.markComplete": "Mark Complete",
    "youth.lessons.resources": "Resources",
    "youth.lessons.pdfDocument": "PDF Document",
    "youth.lessons.externalLink": "External Link",

    // ==================== YOUTH QUIZ ====================
    "youth.quiz.title": "Quiz",
    "youth.quiz.subtitle": "Complete quizzes to unlock the next level",
    "youth.quiz.empty.title": "No quizzes available",
    "youth.quiz.empty.desc": "Quizzes will appear as training levels are published.",
    "youth.quiz.start": "Start Quiz",
    "youth.quiz.back": "Back to Quizzes",
    "youth.quiz.answerAll": "Answer all questions to complete the quiz",
    "youth.quiz.noQuestions": "No questions available",
    "youth.quiz.noQuestionsDesc": "This quiz doesn't have any questions yet.",
    "youth.quiz.submit": "Submit Quiz",
    "youth.quiz.submitting": "Submitting...",
    "youth.quiz.passingScore": "Passing score:",
    "youth.quiz.timeLimit": "Time limit:",
    "youth.quiz.attempts": "Attempts:",
    "youth.quiz.completeVideos": "Complete all videos ({percent}% done) to unlock this quiz.",
    "youth.quiz.completed": "Completed",
    "youth.quiz.alreadyCompleted": "Quiz Already Completed",
    "youth.quiz.alreadyCompletedDesc": "This quiz has already been completed. You cannot retake or access it again.",
    "youth.quiz.passedLocked": "Quiz Passed & Locked!",
    "youth.quiz.passedDesc": "Congratulations! You've successfully completed this level.",
    "youth.quiz.score": "Score:",
    "youth.quiz.permanentlyLocked": "This quiz is now permanently locked. No retakes are allowed.",
    "youth.quiz.failed": "Quiz Failed",
    "youth.quiz.failedDesc": "Don't give up! Review the material and try again.",
    "youth.quiz.retry": "Retry",
    "youth.quiz.result": "Quiz Result",

    // ==================== YOUTH SKILLS ====================
    "youth.skills.title": "Skills",
    "youth.skills.subtitle": "Your acquired skills and proficiency levels",
    "youth.skills.empty.title": "No skills acquired yet",
    "youth.skills.empty.desc": "Complete courses and tasks to earn skills and badges.",
    "youth.skills.summary": "Skills Acquired",
    "youth.skills.acquired": "Acquired:",
    "youth.skills.level.beginner": "Beginner",
    "youth.skills.level.elementary": "Elementary",
    "youth.skills.level.intermediate": "Intermediate",
    "youth.skills.level.advanced": "Advanced",
    "youth.skills.level.expert": "Expert",

    // ==================== YOUTH CERTIFICATES ====================
    "youth.certificates.title": "Certificates",
    "youth.certificates.subtitle": "Your earned credentials and certifications",
    "youth.certificates.earned": "Certificate",
    "youth.certificates.count": "earned",

    // ==================== YOUTH RESOURCES ====================
    "youth.resources.title": "My Resources",
    "youth.resources.subtitle": "Manage your tools and resources",
    "youth.resources.empty.title": "No resources available",
    "youth.resources.empty.desc": "Resources will appear here when available.",
    "youth.resources.complete": "Complete",
    "youth.resources.incomplete": "Incomplete",
    "youth.resources.missingCritical": "Missing Critical",
    "youth.resources.notFilled": "Not Filled",
    "youth.resources.updateSuccess": "Resources updated successfully",
    "youth.resources.updateError": "Error saving resources",
    "youth.resources.fillRequired": "Please fill all required fields",
    "youth.resources.yes": "Yes",
    "youth.resources.no": "No",
    "youth.resources.select": "Select...",
    "youth.resources.available": "Available",
    "youth.resources.notAvailable": "Not Available",
    "youth.resources.required": "Required",
    "youth.resources.notSet": "Not set",
    "youth.resources.critical": "Critical",
    "youth.resources.edit": "Edit",
    "youth.resources.save": "Save",
    "youth.resources.cancel": "Cancel",

    // ==================== PARTNER PORTAL ====================
    "partnerPortal": "Partner Portal",
    "partnerPortalDescription": "Manage your companies and automation services",
    "overview": "Overview",
    "companies": "Companies",
    "requests": "Requests",
    "documents": "Documents",
    "aiDiagnostic": "AI Diagnostic",
    "services": "Services",
    "earnings": "Earnings",
    "settings": "Settings",
    "signOut": "Sign Out",
    
    // ==================== ADMIN MODE SELECTOR ====================
    "admin.modeSelector.welcome": "Welcome",
    "admin.modeSelector.subtitle": "Choose the section you want to access",
    "admin.modeSelector.select": "Select",
    "admin.modeSelector.partners.title": "Partners Management",
    "admin.modeSelector.partners.subtitle": "Manage partner requests and commissions",
    "admin.modeSelector.partners.feature1": "Service Requests",
    "admin.modeSelector.partners.feature2": "Commissions Management",
    "admin.modeSelector.partners.feature3": "Partners Overview",
    "admin.modeSelector.full.title": "Full Admin Dashboard",
    "admin.modeSelector.full.subtitle": "Complete platform management",
    "admin.modeSelector.full.feature1": "User Management",
    "admin.modeSelector.full.feature2": "Training & Certificates",
    "admin.modeSelector.full.feature3": "Analytics & Settings",
    "admin.changeSection": "Change Section",
    "admin.mode.partners": "Partners Mode",
    "admin.mode.full": "Full Admin",
    "admin.signOut": "Sign Out",
    "admin.modeSelector.stats.requests": "Requests",
    "admin.modeSelector.stats.commissions": "Commissions",
    "admin.modeSelector.stats.users": "Users",
    "admin.modeSelector.stats.submissions": "Submissions",

    // ==================== ADMIN SIDEBAR NAV ====================
    "admin.nav.dashboard": "Dashboard",
    "admin.nav.overview": "Overview",
    "admin.nav.users": "Users",
    "admin.nav.allUsers": "All Users",
    "admin.nav.pendingApprovals": "Pending Approvals",
    "admin.nav.training": "Training",
    "admin.nav.classroom": "Classroom",
    "admin.nav.learningPaths": "Learning Paths",
    "admin.nav.meets": "Meets",
    "admin.nav.certificates": "Certificates",
    "admin.nav.tasks": "Tasks",
    "admin.nav.projects": "Projects",
    "admin.nav.opportunities": "Opportunities",
    "admin.nav.resources": "Resources",
    "admin.nav.practicePosts": "Practice Posts",
    "admin.nav.announcements": "Announcements",
    "admin.nav.content": "Content",
    "admin.nav.news": "News",
    "admin.nav.skills": "Skills",
    "admin.nav.skillsManager": "Skills Manager",
    "admin.nav.analytics": "Analytics",
    "admin.nav.youthProgress": "Youth Progress",
    "admin.nav.videoAnalytics": "Video Analytics",
    "admin.nav.partners": "Partners",
    "admin.nav.services": "Services",
    "admin.nav.serviceOptions": "Service Options",
    "admin.nav.serviceRequests": "Service Requests",
    "admin.nav.commissions": "Commissions",
    "admin.nav.partnersOverview": "Partners Overview",
    "admin.nav.management": "Management",
    "admin.nav.platformManagers": "Platform Managers",
    "admin.nav.settings": "Settings",
    "admin.nav.systemSettings": "System Settings",
    "admin.nav.messages": "Messages",
    "admin.nav.contactMessages": "Contact Messages",
    "admin.nav.partnersManagement": "Partners Management",

    // ==================== NEWS MANAGER ====================
    "admin.news.title": "News & Articles",
    "admin.news.subtitle": "Manage articles and news published on the website",
    "admin.news.newArticle": "New Article",
    "admin.news.editArticle": "Edit Article",
    "admin.news.all": "All",
    "admin.news.draft": "Draft",
    "admin.news.published": "Published",
    "admin.news.scheduled": "Scheduled",
    "admin.news.search": "Search...",
    "admin.news.noArticles": "No articles yet",
    "admin.news.views": "views",
    "admin.news.titleRequired": "Title is required",
    "admin.news.saving": "Saving...",
    "admin.news.create": "Create",
    "admin.news.update": "Update",
    "admin.news.cancel": "Cancel",
    "admin.news.showSeo": "Show SEO fields",
    "admin.news.hideSeo": "Hide SEO fields",
    "admin.news.category": "Category",
    "admin.news.tags": "Tags (comma separated)",
    "admin.news.status": "Status",
    "admin.news.scheduledDate": "Scheduled publish date",
    "admin.news.featuredImage": "Featured image URL",
    "admin.news.articleCreated": "Article created",
    "admin.news.articleUpdated": "Article updated",
    "admin.news.articleDeleted": "Article deleted",
    "admin.news.statusUpdated": "Article status updated",
    "admin.news.confirmDelete": "Are you sure you want to delete this article?",
    "welcomeBack": "Welcome back",
    "totalRequests": "Total Requests",
    "pendingRequests": "Pending Requests",
    "totalEarnings": "Total Earnings",
    "addCompany": "Add Company",
    "newRequest": "New Request",
    "recentRequests": "Recent Requests",
    "viewAll": "View All",
    "noRequestsYet": "No requests yet",
    "createFirstRequest": "Create First Request",
    "getStarted": "Get Started",
    "addFirstCompany": "Add your first company",
    "runAiDiagnostic": "Run AI Diagnostic",
    "submitFirstRequest": "Submit first request",
    "earnFirstCommission": "Earn first commission",
    "manageYourCompanies": "Manage your client companies",
    "searchCompanies": "Search companies...",
    "allIndustries": "All Industries",
    "allStatuses": "All Statuses",
    "active": "Active",
    "inactive": "Inactive",
    "noCompaniesYet": "No companies yet",
    "addFirstCompanyDescription": "Add your first client company to start",
    "companyName": "Company Name",
    "enterCompanyName": "Enter company name",
    "industry": "Industry",
    "selectIndustry": "Select industry",
    "companySize": "Company Size",
    "selectSize": "Select size",
    "contactName": "Contact Name",
    "enterContactName": "Enter contact name",
    "phone": "Phone",
    "email": "Email",
    "website": "Website",
    "city": "City",
    "enterCity": "Enter city",
    "notes": "Notes",
    "additionalNotes": "Additional notes...",
    "cancel": "Cancel",
    "save": "Save",
    "saving": "Saving...",
    "editCompany": "Edit Company",
    "deleteCompany": "Delete Company",
    "deleteCompanyConfirmation": "Are you sure you want to delete this company? This action cannot be undone.",
    "delete": "Delete",
    "companyNameRequired": "Company name is required",
    "companyAdded": "Company added successfully",
    "companyUpdated": "Company updated successfully",
    "companyDeleted": "Company deleted successfully",
    "errorFetchingData": "Error fetching data",
    "errorSaving": "Error saving",
    "errorDeleting": "Error deleting",
    "diagnostics": "Diagnostics",
    "serviceRequests": "Service Requests",
    "manageServiceRequests": "Manage your automation and AI service requests",
    "searchRequests": "Search requests...",
    "draft": "Draft",
    "submitted": "Submitted",
    "inReview": "In Review",
    "approved": "Approved",
    "inProgress": "In Progress",
    "completed": "Completed",
    "rejected": "Rejected",
    "newServiceRequest": "New Service Request",
    "company": "Company",
    "selectCompany": "Select company",
    "service": "Service",
    "selectService": "Select service",
    "requestTitle": "Request Title",
    "enterRequestTitle": "Enter request title",
    "description": "Description",
    "describeYourNeeds": "Describe your needs...",
    "priority": "Priority",
    "saveAsDraft": "Save as Draft",
    "submit": "Submit",
    "titleRequired": "Title is required",
    "requestSubmitted": "Request submitted successfully",
    "requestSaved": "Request saved as draft",
    "addCompanyFirst": "Add a company first to create service requests",
    "createFirstRequestDescription": "Create your first automation or AI service request",
    "adminNotes": "Admin Notes",
    "errorSubmitting": "Error submitting request",
    "aiReadinessDiagnostic": "AI Readiness Diagnostic",
    "selectCompanyForDiagnostic": "Select a company to run the diagnostic",
    "noCompaniesForDiagnostic": "Add companies first to run diagnostics",
    "chooseCompany": "Choose a company",
    "step": "Step",
    "complete": "complete",
    "previous": "Previous",
    "next": "Next",
    "showResults": "Show Results",
    "calculating": "Calculating...",
    "diagnosticCompleted": "Diagnostic completed successfully",
    "diagnosticResults": "Diagnostic Results",
    "aiReadinessScore": "AI Readiness Score",
    "recommendations": "Recommendations",
    "startNewDiagnostic": "Start New Diagnostic",
    "requestService": "Request Service",
    "availableServices": "Available Services",
    "browseAndRequestServices": "Browse and request automation & AI services",
    "allCategories": "All Categories",
    "commission": "Commission",
    "request": "Request",
    "addCompanyToRequestServices": "Add a company to request services",
    "additionalDetails": "Additional Details",
    "submitRequest": "Submit Request",
    "submitting": "Submitting...",
    "earningsAndCommissions": "Earnings & Commissions",
    "trackYourEarnings": "Track your earnings from completed projects",
    "paidAmount": "Paid Amount",
    "pendingAmount": "Pending Amount",
    "totalCommissions": "Total Commissions",
    "commissionsHistory": "Commissions History",
    "noCommissionsYet": "No commissions yet",
    "commissionsWillAppear": "Your commissions will appear here when projects are completed",
    "commissionRate": "Commission Rate",
    "amount": "Amount",
    "status": "Status",
    "date": "Date",
    "paid": "Paid",
    "pending": "Pending",
    "paymentInfo": "Payment Information",
    "paymentInfoDescription": "Commissions are processed monthly. Contact admin for payment details.",
    "manageYourAccount": "Manage your account settings",
    "profileInformation": "Profile Information",
    "fullName": "Full Name",
    "enterFullName": "Enter your full name",
    "phoneCannotBeChanged": "Phone number cannot be changed",
    "saveChanges": "Save Changes",
    "profileUpdated": "Profile updated successfully",
    "security": "Security",
    "pinCode": "PIN Code",
    "pinCodeDescription": "Your PIN is used to login",
    "changePin": "Change PIN",
    "contactAdminToChangePin": "Contact admin to change your PIN",
    "accountStatus": "Account Status",
    "partnerAccount": "Partner Account",
    
    // ==================== ADMIN SERVICES MANAGEMENT ====================
    "servicesManagement": "Services Management",
    "servicesManagementDesc": "Manage available services for partners",
    "addService": "Add Service",
    "editService": "Edit Service",
    "serviceName": "Service Name",
    "serviceDescription": "Service Description",
    "category": "Category",
    "icon": "Icon",
    "basePrice": "Base Price",
    "serviceActive": "Service Active",
    "totalServices": "Total Services",
    "activeServices": "Active Services",
    "inactiveServices": "Inactive Services",
    "noServices": "No services yet",
    "addFirstService": "Add First Service",
    "serviceAdded": "Service added successfully",
    "serviceUpdated": "Service updated successfully",
    "serviceDeleted": "Service deleted successfully",
    "deleteServiceConfirm": "Are you sure you want to delete this service? This action cannot be undone.",
    "confirmDelete": "Confirm Delete",
    "errorOccurred": "An error occurred",
    "nameRequired": "Name is required",
    "add": "Add",
    "automation": "Automation",
    "aiAgents": "AI Agents",
    "integrations": "Integrations",
    "consulting": "Consulting",
    "analytics": "Analytics",
    "searchServices": "Search services...",
    "showingResults": "Showing {count} of {total} services",
    "noResultsFound": "No services match your filters",
    "clearFilters": "Clear Filters",

    // ==================== GROWTH REFLECTION ====================
    "growth.reflection.title": "A Moment to Reflect",
    "growth.reflection.subtitle": "Before unlocking the next level, take a short moment to reflect on your journey.",
    "growth.reflection.question": "What idea changed your understanding today?",
    "growth.reflection.placeholder": "Share what stood out to you...",
    "growth.reflection.moodQuestion": "How do you feel right now?",
    "growth.reflection.mood.confident": "Confident",
    "growth.reflection.mood.motivated": "Motivated",
    "growth.reflection.mood.proud": "Proud",
    "growth.reflection.mood.curious": "Curious",
    "growth.reflection.mood.unsure": "Unsure",
    "growth.reflection.growthQuestion": "How much have you grown compared to when you started this level?",
    "growth.reflection.growthLow": "A little",
    "growth.reflection.growthHigh": "Significantly",
    "growth.reflection.optional": "optional",
    "growth.reflection.submit": "Save and continue",

    // ==================== GROWTH EVOLUTION (Phase 2) ====================
    "growth.evolution.title": "Your Growth Evolution",
    "growth.evolution.subtitle": "A private look at how far you've come",
    "growth.evolution.contrast": "Reflection Contrast",
    "growth.evolution.then": "Then",
    "growth.evolution.now": "Now",
    "growth.evolution.moodTimeline": "Mood Evolution",
    "growth.evolution.growthCurve": "Self-Growth Curve",

    // ==================== COMMITMENT INDEX ====================
    "commitment.title": "Reliability Index",
    "commitment.subtext": "Consistency builds professional trust.",
    "commitment.message.high": "Your reliability is exceptionally strong",
    "commitment.message.medium": "You're building trust step by step",
    "commitment.message.low": "Keep going, every action counts",
    "commitment.message.start": "Begin your journey toward consistency",

    // ==================== PROMPTS PAGE ====================
    "prompts.badge": "AI Prompt Library",
    "prompts.title": "Discover Powerful",
    "prompts.titleHighlight": "AI Prompts",
    "prompts.subtitle": "Browse curated prompts for text, image, video, and audio AI models. Copy, vote, and execute instantly.",
    "prompts.search": "Search",
    "prompts.searchPlaceholder": "Search prompts...",
    "prompts.promptType": "Prompt Type",
    "prompts.category": "Category",
    "prompts.allCategories": "All Categories",
    "prompts.sortBy": "Sort By",
    "prompts.resetFilters": "Reset Filters",
    "prompts.loading": "Loading...",
    "prompts.found": "{count} prompt(s) found",
    "prompts.noResults": "No prompts found",
    "prompts.noResultsHint": "Try adjusting your filters",
    "prompts.trending": "Trending",
    "prompts.view": "View",
    "prompts.copy": "Copy",
    "prompts.copied": "Prompt copied to clipboard!",
    "prompts.by": "by",
    "prompts.all": "All",
    "prompts.type.text": "Text",
    "prompts.type.image": "Image",
    "prompts.type.video": "Video",
    "prompts.type.audio": "Audio",
    "prompts.cat.marketing": "Marketing",
    "prompts.cat.automation": "Automation",
    "prompts.cat.web_development": "Web Development",
    "prompts.cat.education": "Education",
    "prompts.cat.data_science": "Data Science",
    "prompts.cat.business": "Business",
    "prompts.cat.ai_agents": "AI Agents",
    "prompts.cat.other": "Other",
    "prompts.sort.trending": "🔥 Trending",
    "prompts.sort.popular": "👍 Most Popular",
    "prompts.sort.copies": "📋 Most Copied",
    "prompts.sort.recent": "🕐 Most Recent",
    "prompts.requireAuth": "You must sign in to use this prompt.",
    "prompts.signIn": "Sign In",

    // ==================== PROMPT DETAIL PAGE ====================
    "prompts.detail.notFound": "Prompt Not Found",
    "prompts.detail.notFoundDesc": "This prompt doesn't exist or has been removed.",
    "prompts.detail.browseAll": "Browse All Prompts",
    "prompts.detail.backToLibrary": "Back to Prompt Library",
    "prompts.detail.upvotes": "Upvotes",
    "prompts.detail.copies": "Copies",
    "prompts.detail.views": "Views",
    "prompts.detail.upvote": "Upvote",
    "prompts.detail.upvoted": "Upvoted",
    "prompts.detail.execute": "Execute",
    "prompts.detail.content": "Prompt Content",
    "prompts.detail.readyTitle": "Ready to use this prompt?",
    "prompts.detail.readyDesc": "Copy it to your clipboard or execute it directly in an AI chat.",
    "prompts.detail.copyPrompt": "Copy Prompt",
    "prompts.detail.executePrompt": "Execute Prompt",
    "prompts.detail.copiedAndOpening": "Prompt copied & opening AI chat!",
  },
  
  fr: {
    // ==================== NAVIGATION ====================
    "nav.about": "Qu'est-ce que SYNRA",
    "nav.services": "Services",
    "nav.training": "Formation",
    "nav.prompts": "Prompts",
    "nav.partners": "Partenaires",
    "nav.signin": "Connexion",
    "nav.getstarted": "Commencer",
    
    // ==================== LANGUAGE SWITCHER ====================
    "lang.change": "Changer de langue",
    "lang.switchTo": "Passer à",
    "lang.en": "Anglais",
    "lang.fr": "Français",
    "lang.ar": "Arabe",
    
    // ==================== COMMON ====================
    "common.loading": "Chargement...",
    "common.error": "Erreur",
    "common.success": "Succès",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.submit": "Soumettre",
    "common.close": "Fermer",
    "common.back": "Retour",
    "common.next": "Suivant",
    "common.continue": "Continuer",
    "common.learnMore": "En savoir plus",
    "common.viewAll": "Voir tout",
    "common.comingSoon": "Bientôt disponible",
    "common.backToHome": "Retour à l'accueil",
    "common.returnToHome": "Retour à l'accueil",
    "common.questions": "Des questions ?",
    "common.contactUs": "Contactez-nous à",

    // ==================== HERO SECTION ====================
    "hero.badge": "Plateforme IA & Automatisation",
    "hero.title": "Transformez Votre Entreprise avec",
    "hero.titleHighlight": "l'IA Pratique",
    "hero.subtitle": "SYNRA permet aux entreprises et aux jeunes marocains de maîtriser l'automatisation et l'intelligence artificielle — de manière structurée, sécurisée et orientée résultats.",
    "hero.cta.partner": "Devenir Partenaire",
    "hero.cta.youth": "Programme Jeunes",
    "hero.stat.partners": "Entreprises Partenaires",
    "hero.stat.automations": "Automatisations Déployées",
    "hero.stat.youth": "Jeunes Formés",

    // ==================== ABOUT SECTION ====================
    "about.label": "À propos de SYNRA",
    "about.title": "IA & Automatisation,",
    "about.titleHighlight": "Rendues Pratiques",
    "about.description1": "SYNRA est plus qu'une plateforme logicielle — c'est un écosystème IA conçu pour le marché marocain. Nous connectons les entreprises en quête de transformation digitale avec des partenaires qualifiés et des talents formés.",
    "about.description2": "Notre mission est de rendre l'IA et l'automatisation accessibles, compréhensibles et actionnables. Que vous soyez une entreprise cherchant à optimiser ses opérations, un consultant élargissant ses services, ou un jeune professionnel développant des compétences d'avenir — SYNRA est votre plateforme.",
    "about.value.trust.title": "Confiance & Sécurité",
    "about.value.trust.desc": "Sécurité de niveau entreprise avec des processus transparents. Vos données et opérations sont protégées.",
    "about.value.results.title": "Résultats Pratiques",
    "about.value.results.desc": "Pas de battage médiatique, juste des résultats mesurables. Nous nous concentrons sur les implémentations qui génèrent un vrai ROI.",
    "about.value.knowledge.title": "Transfert de Connaissances",
    "about.value.knowledge.desc": "Nous ne faisons pas que construire — nous enseignons. Votre équipe acquiert les compétences pour maintenir et étendre les solutions.",
    "about.value.ecosystem.title": "Approche Écosystème",
    "about.value.ecosystem.desc": "Connecter entreprises, partenaires et talents dans une plateforme unifiée pour une croissance durable.",

    // ==================== SERVICES SECTION ====================
    "services.label": "Nos Services",
    "services.title": "Solutions IA Pratiques pour",
    "services.titleHighlight": "des Besoins Réels",
    "services.subtitle": "Nous livrons des solutions d'automatisation et d'IA qui résolvent de vrais problèmes — pas de jargon, juste des résultats.",
    "services.workflow.title": "Automatisation des Workflows",
    "services.workflow.desc": "Éliminez les tâches répétitives et rationalisez les opérations avec des solutions d'automatisation personnalisées. Des workflows simples aux intégrations complexes.",
    "services.workflow.f1": "Cartographie des Processus",
    "services.workflow.f2": "Intégration n8n & Zapier",
    "services.workflow.f3": "Connexions API",
    "services.workflow.f4": "Déclencheurs Personnalisés",
    "services.ai.title": "Agents IA",
    "services.ai.desc": "Déployez des agents intelligents qui comprennent le contexte de votre entreprise. Support client, analyse de données et aide à la décision.",
    "services.ai.f1": "Assistants IA Personnalisés",
    "services.ai.f2": "Intégration NLP",
    "services.ai.f3": "Support Multi-canal",
    "services.ai.f4": "Apprentissage Continu",
    "services.optimization.title": "Optimisation des Systèmes",
    "services.optimization.desc": "Auditez, analysez et optimisez votre stack technologique existant. Identifiez les goulots d'étranglement et implémentez des améliorations.",
    "services.optimization.f1": "Audit de Stack Technologique",
    "services.optimization.f2": "Analyse de Performance",
    "services.optimization.f3": "Revue d'Intégration",
    "services.optimization.f4": "Planification de Scalabilité",

    // ==================== TRAINING SECTION ====================
    "training.label": "Programme de Formation Jeunes",
    "training.title": "Développez de Vraies Compétences,",
    "training.titleHighlight": "Pas Juste de la Théorie",
    "training.subtitle": "Notre programme de formation est conçu pour créer des professionnels prêts à l'emploi. Apprenez en faisant — avec du mentorat, des projets et des parcours de carrière clairs.",
    "training.path.title": "Parcours d'Apprentissage",
    "training.level.beginner": "Débutant",
    "training.level.intermediate": "Intermédiaire",
    "training.level.advanced": "Avancé",
    "training.level.expert": "Expert",
    "training.course.fundamentals": "Fondamentaux de l'Automatisation",
    "training.course.n8n": "n8n & Workflows Avancés",
    "training.course.agents": "Développement d'Agents IA",
    "training.course.projects": "Projets Réels",
    "training.duration.weeks": "semaines",
    "training.duration.ongoing": "En cours",
    "training.skill.workflow": "Logique de Workflow",
    "training.skill.integrations": "Intégrations de Base",
    "training.skill.nocode": "Outils No-Code",
    "training.skill.n8n": "Maîtrise de n8n",
    "training.skill.api": "Intégration API",
    "training.skill.errors": "Gestion des Erreurs",
    "training.skill.llm": "Intégration LLM",
    "training.skill.agents": "Agents Personnalisés",
    "training.skill.deployment": "Déploiement Production",
    "training.skill.client": "Projets Clients",
    "training.skill.team": "Collaboration d'Équipe",
    "training.skill.industry": "Solutions Sectorielles",
    "training.benefit.curriculum.title": "Curriculum Structuré",
    "training.benefit.curriculum.desc": "Progression claire des bases aux concepts avancés",
    "training.benefit.projects.title": "Vrais Projets",
    "training.benefit.projects.desc": "Travaillez sur de vrais projets d'entreprise, pas juste des exercices",
    "training.benefit.certification.title": "Certification des Compétences",
    "training.benefit.certification.desc": "Obtenez des credentials reconnus au fur et à mesure de votre progression",
    "training.cta.title": "Prêt à Commencer Votre Parcours ?",
    "training.cta.desc": "Rejoignez notre programme jeunes et acquérez les compétences que les entreprises recherchent activement. Places limitées par cohorte.",
    "training.cta.button": "Postuler Maintenant",

    // ==================== PARTNERS SECTION ====================
    "partners.label": "Programme Partenaires",
    "partners.title": "Développez Votre Business avec",
    "partners.titleHighlight": "SYNRA",
    "partners.subtitle": "Rejoignez notre réseau de partenaires et offrez des services IA & automatisation de niveau entreprise à vos clients. Nous fournissons la technologie, vous fournissez la relation.",
    "partners.ideal.title": "Partenaires Idéaux",
    "partners.type.agencies": "Agences",
    "partners.type.agencies.desc": "Agences de marketing digital et web se développant dans l'automatisation",
    "partners.type.consultants": "Consultants",
    "partners.type.consultants.desc": "Consultants business ajoutant l'IA à leurs offres de services",
    "partners.type.it": "Entreprises IT",
    "partners.type.it.desc": "Entreprises technologiques cherchant des solutions d'automatisation éprouvées",
    "partners.cta": "Devenir Partenaire",
    "partners.benefit.whitelabel.title": "Solutions en Marque Blanche",
    "partners.benefit.whitelabel.desc": "Vendez les services SYNRA sous votre marque. Portail partenaire complet pour gérer vos clients.",
    "partners.benefit.revenue.title": "Partage des Revenus",
    "partners.benefit.revenue.desc": "Structure de commission compétitive. Suivez vos gains et paiements en temps réel.",
    "partners.benefit.sales.title": "Support Commercial",
    "partners.benefit.sales.desc": "Accédez aux présentations, propositions et modèles. Nous vous aidons à conclure des affaires.",
    "partners.benefit.dashboard.title": "Tableau de Bord Client",
    "partners.benefit.dashboard.desc": "Gérez vos entreprises, suivez le statut des automatisations et surveillez les résultats.",

    // ==================== TESTIMONIALS ====================
    "testimonials.badge": "Histoires de Réussite",
    "testimonials.title": "Ce que Disent Nos Diplômés",
    "testimonials.subtitle": "Des résultats réels de personnes qui ont transformé leur carrière avec SYNRA.",
    "testimonials.1.name": "Yassine El Amrani",
    "testimonials.1.sector": "Marketing Digital — Casablanca",
    "testimonials.1.quote": "Grâce à SYNRA, j'ai automatisé 80% des rapports de mon agence. Notre productivité a doublé en seulement 3 mois.",
    "testimonials.2.name": "Fatima Zahra Bennani",
    "testimonials.2.sector": "E-commerce — Rabat",
    "testimonials.2.quote": "La formation en IA m'a donné les compétences pour créer des chatbots pour ma boutique en ligne. La satisfaction client a augmenté de 45%.",
    "testimonials.3.name": "Omar Tazi",
    "testimonials.3.sector": "Technologie Éducative — Marrakech",
    "testimonials.3.quote": "Le programme SYNRA m'a aidé à passer de l'enseignement traditionnel à l'éducation propulsée par l'IA. Je forme maintenant d'autres enseignants.",

    // ==================== CTA SECTION ====================
    "cta.badge": "Commencez Votre Parcours IA",
    "cta.title": "Prêt à Transformer Votre",
    "cta.titleHighlight": "Entreprise ?",
    "cta.subtitle": "Que vous soyez une entreprise cherchant l'automatisation, un partenaire cherchant à se développer, ou un jeune talent développant ses compétences — SYNRA est votre plateforme.",
    "cta.partner": "Devenir Partenaire",
    "cta.training": "Postuler à la Formation",

    // ==================== FOOTER ====================
    "footer.tagline": "Accompagner les entreprises et les jeunes marocains avec des solutions pratiques d'IA & Automatisation.",
    "footer.platform": "Plateforme",
    "footer.platform.services": "Services",
    "footer.platform.training": "Formation",
    "footer.platform.partners": "Partenaires",
    "footer.platform.pricing": "Tarifs",
    "footer.company": "Entreprise",
    "footer.company.about": "À propos",
    "footer.company.careers": "Carrières",
    "footer.company.blog": "Blog",
    "footer.company.contact": "Contact",
    "footer.legal": "Légal",
    "footer.legal.privacy": "Politique de Confidentialité",
    "footer.legal.terms": "Conditions d'Utilisation",
    "footer.legal.cookies": "Politique de Cookies",
    "footer.copyright": "© {year} SYNRA. Tous droits réservés.",
    "footer.madeIn": "Fait avec précision au Maroc",

    // ==================== AUTH PAGE ====================
    "auth.welcome.title": "Bienvenue dans Votre",
    "auth.welcome.highlight": "Parcours IA",
    "auth.welcome.subtitle": "Rejoignez le premier écosystème IA & Automatisation du Maroc. Que vous soyez un partenaire business ou un talent en devenir, SYNRA est votre plateforme de croissance.",
    "auth.card.partners.title": "Partenaires",
    "auth.card.partners.desc": "Développez vos services avec des solutions IA & Automatisation",
    "auth.card.youth.title": "Jeunes",
    "auth.card.youth.desc": "Développez de vraies compétences grâce à une formation pratique",
    "auth.tab.signin": "Connexion",
    "auth.tab.partner": "Partenaire",
    "auth.tab.youth": "Jeunes",
    "auth.signin.title": "Bon Retour",
    "auth.signin.subtitle": "Connectez-vous avec votre numéro de téléphone et PIN",
    "auth.partner.title": "Devenir Partenaire",
    "auth.partner.subtitle": "Rejoignez notre réseau et offrez des services IA à vos clients",
    "auth.youth.title": "Rejoindre le Programme de Formation",
    "auth.youth.subtitle": "Commencez votre parcours pour devenir un professionnel de l'IA",
    "auth.backToHome": "Retour à l'accueil",

    // ==================== 404 PAGE ====================
    "notfound.title": "404",
    "notfound.message": "Oups ! Page non trouvée",
    "notfound.link": "Retour à l'accueil",

    // ==================== YOUTH SIDEBAR ====================
    "youth.nav.main": "Principal",
    "youth.nav.learning": "Apprentissage",
    "youth.nav.progress": "Progrès & Réalisations",
    "youth.nav.resources": "Ressources & Croissance",
    "youth.home": "Accueil",
    "youth.classroom": "Salle de Classe",
    "youth.lessons": "Leçons",
    "youth.paths": "Parcours d'Apprentissage",
    "youth.quiz": "Quiz",
    "youth.progress": "Progression",
    "youth.certificates": "Certificats",
    "youth.skills": "Compétences",
    "youth.resources": "Ressources",
    "youth.practicePosts": "Posts Pratiques",
    "youth.projects": "Projets",
    "youth.opportunities": "Opportunités",
    "youth.meet": "Rencontres",
    "youth.chat": "Chat Communautaire",
     "youth.tasks": "Tâches",
     "youth.signout": "Déconnexion",

    // ==================== YOUTH HOME ====================
    "youth.home.title": "Bienvenue",
    "youth.home.subtitle": "Votre Parcours de Formation IA Commence Ici",
    "youth.home.platform": "Plateforme SYNRA",
    "youth.home.greeting": "Bienvenue, {name} !",
    "youth.home.description": "Maîtrisez les compétences en IA et automatisation grâce à une formation structurée et professionnelle. Développez une expertise concrète et débloquez de nouvelles opportunités.",
    "youth.home.cta.classroom": "Aller en Classe",
    "youth.home.cta.paths": "Voir les Parcours",
    "youth.home.card.learning.title": "Apprentissage Structuré",
    "youth.home.card.learning.desc": "Progressez à travers des niveaux soigneusement conçus, du débutant à l'avancé, avec des vidéos et de la pratique.",
    "youth.home.card.projects.title": "Projets Réels",
    "youth.home.card.projects.desc": "Appliquez vos connaissances à de vrais projets et construisez un portfolio qui met en valeur vos compétences.",
    "youth.home.card.career.title": "Opportunités de Carrière",
    "youth.home.card.career.desc": "Obtenez des certificats, débloquez des opportunités et connectez-vous avec des entreprises recherchant des professionnels IA.",
    "youth.home.coming.label": "Bientôt Disponible",
    "youth.home.coming.title": "Nouvelles Fonctionnalités & Contenus",
    "youth.home.coming.desc": "Nous ajoutons constamment de nouveaux modules de formation, opportunités et fonctionnalités pour vous aider à avancer. Restez connecté !",

    // ==================== YOUTH CLASSROOM ====================
    "youth.classroom.title": "Salle de Classe",
    "youth.classroom.subtitle": "Vos programmes inscrits et votre progression",
    "youth.classroom.loading": "Chargement de vos programmes...",
    "youth.classroom.stats.total": "Niveaux Totaux",
    "youth.classroom.stats.inProgress": "En Cours",
    "youth.classroom.stats.completed": "Terminés",
    "youth.classroom.empty.title": "Aucun programme disponible",
    "youth.classroom.empty.desc": "Le contenu de formation est en préparation. Revenez bientôt pour commencer votre parcours.",
    "youth.classroom.levels.title": "Vos Niveaux d'Apprentissage",
    "youth.classroom.viewPaths": "Voir Tous les Parcours",
    "youth.classroom.lessons": "leçons",
    "youth.classroom.min": "min",
    "youth.classroom.complete": "terminé",
    "youth.classroom.back": "Retour aux Niveaux",
    "youth.classroom.yourProgress": "Votre Progression",
    "youth.classroom.contentPreparing": "Le contenu est en préparation",
    "youth.classroom.checkBack": "Revenez bientôt.",
    "youth.classroom.lessonsTitle": "Leçons",
    "youth.classroom.allComplete.title": "Toutes les leçons terminées !",
    "youth.classroom.allComplete.desc": "Prêt à tester vos connaissances ?",
    "youth.classroom.takeQuiz": "Passer le Quiz",
    "youth.classroom.selectLesson": "Sélectionnez une leçon",
    "youth.classroom.chooseLesson": "Choisissez une leçon dans la liste pour commencer",
    "youth.classroom.markComplete": "Marquer Terminé",
    "youth.classroom.completed": "Terminé",
    "youth.classroom.watchToComplete": "Regardez la vidéo pour terminer",
    "youth.classroom.replay": "Rejouer",
    "youth.classroom.requiredWatch": "Temps de visionnage requis",
    "youth.classroom.seekBlocked": "L'avance rapide n'est pas autorisée",
    "youth.classroom.noSkip": "Pas de saut",
    "youth.classroom.watchedTime": "Temps visionné",
    "youth.classroom.unlocked": "Débloqué",
    "youth.classroom.remaining": "restant",
    
    // ==================== SMART CONTEXT PANEL (FR) ====================
    "youth.classroom.contextPanel.title": "Contexte d'apprentissage",
    "youth.classroom.contextPanel.currentContext": "Contexte actuel",
    "youth.classroom.contextPanel.upcoming": "À venir",
    "youth.classroom.contextPanel.previous": "Précédent",
    "youth.classroom.contextPanel.openLink": "Ouvrir le lien",
    "youth.classroom.contextPanel.waitingForContext": "Le contexte apparaîtra pendant le visionnage",
    "youth.classroom.contextPanel.types.platform": "Plateforme",
    "youth.classroom.contextPanel.types.tool": "Outil",
    "youth.classroom.contextPanel.types.documentation": "Documentation",
    "youth.classroom.contextPanel.types.note": "Note",
    "youth.classroom.contextPanel.types.exercise": "Exercice",

    // ==================== DIFFICULTY LEVELS ====================
    "difficulty.beginner": "Débutant",
    "difficulty.intermediate": "Intermédiaire",
    "difficulty.advanced": "Avancé",

    // ==================== EMPTY STATES ====================
    "empty.noData": "Aucune donnée disponible",
    "empty.noLessons": "Aucune leçon disponible",
    "empty.noCertificates": "Aucun certificat obtenu",
    "empty.noOpportunities": "Aucune opportunité disponible",
    "empty.noProjects": "Aucun projet assigné",
    "empty.noTasks": "Aucune tâche assignée",
    "empty.noMeets": "Aucune réunion programmée",
    "empty.noSkills": "Aucune compétence enregistrée",
    "empty.noResources": "Aucune ressource disponible",

    // ==================== ERRORS ====================
    "error.generic": "Une erreur s'est produite",
    "error.notFound": "Page non trouvée",
    "error.unauthorized": "Accès non autorisé",
    "error.network": "Erreur réseau. Veuillez réessayer.",
    "error.loadFailed": "Échec du chargement des données",
    "error.validation": "Erreur de validation",
    "error.loginFailed": "Échec de connexion",
    "error.invalidCredentials": "Numéro de téléphone ou PIN invalide. Veuillez réessayer.",
    "error.pendingConfirmation": "Votre compte est en attente de confirmation.",
    "error.registrationFailed": "Échec de l'inscription",
    "error.alreadyRegistered": "Ce numéro de téléphone est déjà enregistré. Veuillez vous connecter.",
    "error.profileCreationFailed": "Impossible de créer le profil. Veuillez réessayer.",
    "error.userCreationFailed": "Impossible de créer le compte utilisateur.",
    "error.unexpected": "Une erreur inattendue s'est produite. Veuillez réessayer.",

    // ==================== AUTH FORMS ====================
    "auth.form.phone": "Numéro de téléphone",
    "auth.form.phone.placeholder": "+212 6XX XXX XXX",
    "auth.form.pin": "PIN (4-6 chiffres)",
    "auth.form.pin.placeholder": "••••••",
    "auth.form.fullName": "Nom complet",
    "auth.form.fullName.placeholder.partner": "Ahmed El Mansouri",
    "auth.form.fullName.placeholder.youth": "Youssef Benjelloun",
    "auth.form.companyName": "Nom de l'entreprise",
    "auth.form.companyName.placeholder": "Votre Société SARL",
    "auth.form.domain": "Domaine d'activité",
    "auth.form.domain.placeholder": "ex: Marketing, Services IT",
    "auth.form.email": "Email (Optionnel)",
    "auth.form.email.placeholder": "ahmed@entreprise.ma",
    "auth.form.city": "Ville",
    "auth.form.city.placeholder": "Casablanca",
    "auth.form.level": "Niveau actuel",
    "auth.form.level.beginner": "Débutant - Aucune expérience",
    "auth.form.level.intermediate": "Intermédiaire - Quelques connaissances",
    "auth.form.level.advanced": "Avancé - Expérimenté",
    "auth.form.required": "*",
    "auth.form.signin": "Se connecter",
    "auth.form.signingIn": "Connexion en cours...",
    "auth.form.createAccount": "Créer un compte",
    "auth.form.creatingAccount": "Création du compte...",
    "auth.form.welcomeBack": "Bon retour !",
    "auth.form.loginSuccess": "Connexion réussie.",

    // ==================== PASSWORD CONFIRMATION ====================
    "auth.password.title": "Votre PIN d'accès",
    "auth.password.subtitle": "Sauvegardez ce PIN en sécurité. Vous en aurez besoin pour vous connecter.",
    "auth.password.warning": "Ce PIN ne sera PAS affiché à nouveau !",
    "auth.password.copied": "PIN copié dans le presse-papiers !",
    "auth.password.copy": "Copier le PIN",
    "auth.password.confirm": "J'ai sauvegardé mon PIN",

    // ==================== PENDING APPROVAL ====================
    "auth.pending.title": "Candidature soumise",
    "auth.pending.greeting": "Merci, {name} !",
    "auth.pending.message": "Votre candidature est en cours d'examen par notre équipe. Vous recevrez une notification une fois votre compte approuvé.",
    "auth.pending.signin": "Aller à la connexion",

    // ==================== YOUTH DASHBOARD PAGES (FR) ====================
    "youth.paths.title": "Parcours d'Apprentissage",
    "youth.paths.subtitle": "Programmes structurés pour développer votre expertise IA",
    "youth.paths.empty.title": "Aucun parcours disponible",
    "youth.paths.empty.desc": "Les programmes sont en préparation. Revenez plus tard.",
    "youth.paths.continue": "Continuer",
    "youth.paths.available": "Parcours Disponibles",
    "youth.paths.completed": "Terminés",
    "youth.paths.locked": "Verrouillés",
    "youth.paths.inProgress": "En Cours",
    "youth.paths.modules": "modules",
    "youth.paths.startPath": "Commencer",
    "youth.paths.review": "Revoir",
    "youth.paths.unlockMessage": "Terminez les parcours précédents",
    "youth.opportunities.title": "Opportunités",
    "youth.opportunities.subtitle": "Emplois et stages adaptés à vos compétences",
    "youth.opportunities.empty.title": "Aucune opportunité",
    "youth.opportunities.empty.desc": "De nouvelles opportunités apparaîtront ici.",
    "youth.opportunities.available": "Disponibles pour Vous",
    "youth.opportunities.locked": "Terminer les Niveaux Requis",
    "youth.opportunities.apply": "Postuler",
    "youth.opportunities.remote": "À Distance",
    "youth.opportunities.requiredLevels": "Niveaux Requis:",
    "youth.projects.title": "Projets",
    "youth.projects.subtitle": "Projets réels pour construire votre portfolio",
    "youth.projects.empty.title": "Aucun projet assigné",
    "youth.projects.empty.desc": "Les projets apparaîtront ici.",
    "youth.projects.active": "Projets Actifs",
    "youth.projects.completed": "Projets Terminés",
    "youth.projects.role": "Rôle:",
    "youth.projects.started": "Commencé:",
    "youth.projects.status.active": "Actif",
    "youth.projects.status.completed": "Terminé",
    "youth.projects.type.internal": "Interne",
    "youth.projects.type.company": "Entreprise",
    "youth.projects.type.simulation": "Simulation",
    "youth.projects.view": "Voir le Projet",
    "youth.projects.progress": "Progression",
    "youth.projects.milestones": "Étapes du Projet",
    "youth.projects.resources": "Ressources du Projet",
    "youth.projects.noMilestones": "Aucune étape définie pour ce projet",
    "youth.projects.noResources": "Aucune ressource disponible",
    "youth.projects.submit": "Soumettre Votre Travail",
    "youth.projects.pending": "Votre soumission est en cours d'examen",
    "youth.projects.instructions": "Instructions de Soumission",
    "youth.projects.notes": "Description du Travail",
    "youth.projects.notesPlaceholder": "Décrivez votre travail...",
    "youth.projects.url": "Lien du Projet",
    "youth.projects.submitBtn": "Soumettre le Projet",
    "youth.projects.submitting": "Soumission en cours...",
    "youth.projects.submissions": "Soumissions Précédentes",
    "youth.projects.feedback": "Commentaires",
    "youth.projects.dueDate": "Date d'Échéance",
    "youth.projects.deadline": "Date Limite",
    "youth.projects.daysRemaining": "jours restants",
    "youth.projects.overdue": "En Retard",
    "youth.projects.dueToday": "Expire Aujourd'hui",
    "youth.projects.dueSoon": "Expire Bientôt",
    "youth.projects.urgentWarning": "Plus que {days} jours!",
    "youth.projects.notFound": "Projet introuvable ou non assigné",
    "youth.projects.status.pending_review": "En Révision",
    "youth.projects.status.approved": "Approuvé",
    "youth.projects.status.needs_revision": "Révision Nécessaire",
    "youth.projects.status.rejected": "Rejeté",
    "youth.progress.title": "Progrès & Évaluation",
    "youth.progress.subtitle": "Suivez votre parcours d'apprentissage",
    "youth.progress.overall": "Progrès Global",
    "youth.progress.avgScore": "Score Moyen",
    "youth.progress.currentLevel": "Niveau Actuel",
    "youth.progress.classroomLevels": "Niveaux de Classe",
    "youth.progress.learningPaths": "Parcours",
    "youth.progress.completed": "Terminés",
    "youth.progress.inProgress": "En Cours",
    "youth.progress.available": "Disponibles",
    "youth.progress.totalLevels": "Niveaux Totaux",
    "youth.progress.videos": "Vidéos",
    "youth.progress.quiz": "Quiz",
    "youth.progress.passed": "Réussi",
    "youth.progress.notPassed": "Non Réussi",
    "youth.progress.taskCompletion": "Achèvement des Tâches",
    "youth.progress.recentEvaluations": "Évaluations Récentes",
    "youth.progress.noEvaluations": "Aucune évaluation",
    "youth.progress.of": "de",
    "youth.progress.personalDev": "Développement Personnel",
    "youth.tasks.title": "Tâches",
    "youth.tasks.subtitle": "Tâches assignées et soumissions",
    "youth.tasks.empty.title": "Aucune tâche assignée",
    "youth.tasks.empty.desc": "Les tâches apparaîtront ici.",
    "youth.tasks.empty.caughtUp": "Vous êtes à jour.",
    "youth.tasks.empty.newAssignments": "Les nouvelles tâches apparaîtront ici.",
    "youth.tasks.pending": "En Attente",
    "youth.tasks.underReview": "En Révision",
    "youth.tasks.completed": "Terminées",
    "youth.tasks.submit": "Soumettre",
    "youth.tasks.resubmit": "Resoumettre",
    "youth.tasks.submitTask": "Soumettre la Tâche",
    "youth.tasks.due": "Échéance:",
    "youth.tasks.feedback": "Retour",
    "youth.tasks.overdue": "En retard",
    "youth.tasks.completion": "Achèvement",
    "youth.tasks.reliabilityNote": "Impact sur l'indice",
    "youth.tasks.reliabilityWarning": "Les soumissions en retard peuvent affecter votre indice de fiabilité.",
    "youth.tasks.noTasksFilter": "Aucune tâche ne correspond à ce filtre.",
    "youth.tasks.filter.all": "Toutes",
    "youth.tasks.filter.pending": "En attente",
    "youth.tasks.filter.submitted": "Soumises",
    "youth.tasks.filter.approved": "Approuvées",
    "youth.tasks.filter.overdue": "En retard",
    "youth.tasks.status.approved": "Approuvé",
    "youth.tasks.status.submitted": "Soumis",
    "youth.tasks.status.needsRevision": "À réviser",
    "youth.tasks.status.pending": "En attente",
    "youth.tasks.status.rejected": "Rejeté",
    "youth.tasks.filter.active": "Actives",
    "youth.tasks.filter.underReview": "En révision",
    "youth.tasks.filter.needsRevision": "À réviser",
    "youth.tasks.filter.completed": "Terminées",
    "youth.tasks.empty.levelUnlock": "Complétez un niveau pour débloquer les tâches appliquées.",
    "youth.tasks.submitting": "Soumission...",
    "youth.tasks.submissionText": "Texte de soumission",
    "youth.tasks.link": "Lien (optionnel)",
    "youth.tasks.submitted": "Soumis:",
    "youth.tasks.describe": "Décrivez votre travail ou collez votre réponse...",

    // ==================== YOUTH PRACTICE POSTS ====================
    "youth.practicePosts.title": "Posts Pratiques",
    "youth.practicePosts.subtitle": "Ressources d'apprentissage interactives",
    "youth.practicePosts.platformLinks": "Liens de Plateforme",
    "youth.practicePosts.steps": "Étapes",
    "youth.practicePosts.comments": "Commentaires",
    "youth.practicePosts.addComment": "Ajouter un commentaire...",
    "youth.practicePosts.noComments": "Pas encore de commentaires. Soyez le premier !",
    "youth.practicePosts.noPosts": "Aucun post pratique disponible",


    // ==================== YOUTH COMMENTS ====================
    "youth.comments.title": "Commentaires",
    "youth.comments.placeholder": "Écrivez votre commentaire ou question ici...",
    "youth.comments.reply": "Répondre",
    "youth.comments.delete": "Supprimer",
    "youth.comments.replyPlaceholder": "Écrivez votre réponse...",
    "youth.comments.empty": "Pas encore de commentaires. Soyez le premier !",
    "youth.comments.error": "Une erreur est survenue",
    "youth.comments.deleteError": "Erreur lors de la suppression",

    // ==================== YOUTH ROADMAP ====================
    "youth.roadmap.title": "Votre Parcours d'Apprentissage",
    "youth.roadmap.subtitle": "Complétez les 12 niveaux pour devenir un expert certifié",

    // ==================== YOUTH CHAT (FR) ====================
    "youth.chat.title": "Communauté de la Plateforme",
    "youth.chat.subtitle": "Connectez-vous avec tous les membres",
    "youth.chat.members": "membres en ligne",
    "youth.chat.emptyGroup": "Pas encore de messages",
    "youth.chat.emptyGroupHint": "Soyez le premier à envoyer un message dans la communauté !",
    "youth.chat.messagePlaceholder": "Écrivez votre message...",

    // ==================== NOTIFICATIONS (FR) ====================
    "notifications": "Notifications",
    "markAllRead": "Tout marquer comme lu",
    "noNotifications": "Aucune notification",

    "youth.meet.title": "Rencontres",
    "youth.meet.subtitle": "Sessions en direct avec les instructeurs",
    "youth.meet.empty.title": "Aucune session disponible",
    "youth.meet.empty.desc": "Les sessions apparaîtront ici.",
    "youth.meet.upcoming": "Sessions à Venir",
    "youth.meet.past": "Sessions Passées",
    "youth.meet.today": "Aujourd'hui",
    "youth.meet.joined": "Rejoint",
    "youth.meet.attended": "Présent",
    "youth.meet.join": "Rejoindre",
    "youth.meet.rejoin": "Rejoindre à nouveau",
    "youth.meet.notAvailable": "Pas encore disponible",
    "youth.meet.attendanceRecorded": "Présence enregistrée",
    "youth.meet.attendanceDesc": "Votre présence a été enregistrée pour cette session.",
    "youth.meet.session": "Session",
    "youth.meet.openNewTab": "Cette session s'ouvrira dans un nouvel onglet. Assurez-vous que votre caméra et microphone sont prêts.",
    "youth.meet.joinLive": "Rejoindre la session en direct",
    "youth.meet.opensNewTab": "S'ouvre dans un nouvel onglet",
    "youth.meet.minSession": "min de session",
    "youth.meet.ended": "Terminée",
    "youth.lessons.title": "Leçons",
    "youth.lessons.subtitle": "Contenu d'apprentissage et ressources",
    "youth.lessons.empty.title": "Aucune leçon disponible",
    "youth.lessons.empty.desc": "Commencez un parcours pour accéder aux leçons.",
    "youth.lessons.browsePaths": "Parcourir les Parcours",
    "youth.lessons.selectLesson": "Sélectionnez une leçon",
    "youth.lessons.markComplete": "Marquer Terminé",
    "youth.lessons.resources": "Ressources",
    "youth.quiz.title": "Quiz",
    "youth.quiz.subtitle": "Complétez les quiz pour débloquer le niveau suivant",
    "youth.quiz.empty.title": "Aucun quiz disponible",
    "youth.quiz.empty.desc": "Les quiz apparaîtront ici.",
    "youth.quiz.start": "Commencer",
    "youth.quiz.back": "Retour aux Quiz",
    "youth.quiz.answerAll": "Répondez à toutes les questions pour terminer le quiz",
    "youth.quiz.noQuestions": "Aucune question disponible",
    "youth.quiz.noQuestionsDesc": "Ce quiz n'a pas encore de questions.",
    "youth.quiz.submit": "Soumettre le Quiz",
    "youth.quiz.submitting": "Soumission...",
    "youth.quiz.passingScore": "Score requis:",
    "youth.quiz.timeLimit": "Temps limite:",
    "youth.quiz.attempts": "Tentatives:",
    "youth.quiz.completeVideos": "Complétez toutes les vidéos ({percent}% fait) pour débloquer ce quiz.",
    "youth.quiz.completed": "Terminé",
    "youth.quiz.alreadyCompleted": "Quiz déjà terminé",
    "youth.quiz.alreadyCompletedDesc": "Ce quiz a déjà été complété. Vous ne pouvez pas le repasser.",
    "youth.quiz.passedLocked": "Quiz réussi et verrouillé !",
    "youth.quiz.passedDesc": "Félicitations ! Vous avez terminé ce niveau avec succès.",
    "youth.quiz.score": "Score:",
    "youth.quiz.permanentlyLocked": "Ce quiz est maintenant verrouillé définitivement. Aucune reprise n'est autorisée.",
    "youth.quiz.failed": "Quiz échoué",
    "youth.quiz.failedDesc": "N'abandonnez pas ! Révisez le contenu et réessayez.",
    "youth.quiz.retry": "Réessayer",
    "youth.quiz.result": "Résultat du Quiz",
    "youth.skills.title": "Compétences",
    "youth.skills.subtitle": "Vos compétences acquises",
    "youth.skills.empty.title": "Aucune compétence acquise",
    "youth.skills.empty.desc": "Complétez des cours pour gagner des compétences.",
    "youth.skills.summary": "Compétences Acquises",
    "youth.skills.acquired": "Acquis:",
    "youth.skills.level.beginner": "Débutant",
    "youth.skills.level.elementary": "Élémentaire",
    "youth.skills.level.intermediate": "Intermédiaire",
    "youth.skills.level.advanced": "Avancé",
    "youth.skills.level.expert": "Expert",
    "youth.certificates.title": "Certificats",
    "youth.certificates.subtitle": "Vos certifications obtenues",
    "youth.certificates.earned": "Certificat(s) obtenu(s)",
    "youth.certificates.count": "obtenu(s)",

    // ==================== YOUTH RESOURCES (FR) ====================
    "youth.resources.title": "Mes Ressources",
    "youth.resources.subtitle": "Gérez vos outils et ressources",
    "youth.resources.empty.title": "Aucune ressource disponible",
    "youth.resources.empty.desc": "Les ressources apparaîtront ici.",
    "youth.resources.complete": "Complet",
    "youth.resources.incomplete": "Incomplet",
    "youth.resources.missingCritical": "Ressources critiques manquantes",
    "youth.resources.notFilled": "Non rempli",
    "youth.resources.updateSuccess": "Ressources mises à jour",
    "youth.resources.updateError": "Erreur de sauvegarde",
    "youth.resources.fillRequired": "Veuillez remplir tous les champs obligatoires",
    "youth.resources.yes": "Oui",
    "youth.resources.no": "Non",
    "youth.resources.select": "Sélectionner...",
    "youth.resources.available": "Disponible",
    "youth.resources.notAvailable": "Non disponible",
    "youth.resources.required": "Requis",
    "youth.resources.notSet": "Non défini",
    "youth.resources.critical": "Critique",
    "youth.resources.edit": "Modifier",
    "youth.resources.save": "Enregistrer",
    "youth.resources.cancel": "Annuler",

    // ==================== PARTNER PORTAL ====================
    "partnerPortal": "Portail Partenaire",
    "partnerPortalDescription": "Gérez vos entreprises et services d'automatisation",
    "overview": "Aperçu",
    "companies": "Entreprises",
    "requests": "Demandes",
    "documents": "Documents",
    "aiDiagnostic": "Diagnostic IA",
    "services": "Services",
    "earnings": "Revenus",
    "settings": "Paramètres",
    "signOut": "Déconnexion",
    
    // ==================== ADMIN MODE SELECTOR ====================
    "admin.modeSelector.welcome": "Bienvenue",
    "admin.modeSelector.subtitle": "Choisissez la section à laquelle vous souhaitez accéder",
    "admin.modeSelector.select": "Sélectionner",
    "admin.modeSelector.partners.title": "Gestion des Partenaires",
    "admin.modeSelector.partners.subtitle": "Gérer les demandes et commissions des partenaires",
    "admin.modeSelector.partners.feature1": "Demandes de Services",
    "admin.modeSelector.partners.feature2": "Gestion des Commissions",
    "admin.modeSelector.partners.feature3": "Aperçu des Partenaires",
    "admin.modeSelector.full.title": "Tableau de Bord Complet",
    "admin.modeSelector.full.subtitle": "Gestion complète de la plateforme",
    "admin.modeSelector.full.feature1": "Gestion des Utilisateurs",
    "admin.modeSelector.full.feature2": "Formation et Certificats",
    "admin.modeSelector.full.feature3": "Analyses et Paramètres",
    "admin.changeSection": "Changer de Section",
    "admin.mode.partners": "Mode Partenaires",
    "admin.mode.full": "Admin Complet",
    "admin.signOut": "Déconnexion",
    "admin.modeSelector.stats.requests": "Demandes",
    "admin.modeSelector.stats.commissions": "Commissions",
    "admin.modeSelector.stats.users": "Utilisateurs",
    "admin.modeSelector.stats.submissions": "Soumissions",

    // ==================== ADMIN SIDEBAR NAV ====================
    "admin.nav.dashboard": "Tableau de bord",
    "admin.nav.overview": "Vue d'ensemble",
    "admin.nav.users": "Utilisateurs",
    "admin.nav.allUsers": "Tous les utilisateurs",
    "admin.nav.pendingApprovals": "Approbations en attente",
    "admin.nav.training": "Formation",
    "admin.nav.classroom": "Salle de classe",
    "admin.nav.learningPaths": "Parcours d'apprentissage",
    "admin.nav.meets": "Reunions",
    "admin.nav.certificates": "Certificats",
    "admin.nav.tasks": "Taches",
    "admin.nav.projects": "Projets",
    "admin.nav.opportunities": "Opportunites",
    "admin.nav.resources": "Ressources",
    "admin.nav.practicePosts": "Posts pratiques",
    "admin.nav.announcements": "Annonces",
    "admin.nav.content": "Contenu",
    "admin.nav.news": "Actualites",
    "admin.nav.skills": "Competences",
    "admin.nav.skillsManager": "Gestionnaire de competences",
    "admin.nav.analytics": "Analyses",
    "admin.nav.youthProgress": "Progres des jeunes",
    "admin.nav.videoAnalytics": "Analyses video",
    "admin.nav.partners": "Partenaires",
    "admin.nav.services": "Services",
    "admin.nav.serviceOptions": "Options de services",
    "admin.nav.serviceRequests": "Demandes de services",
    "admin.nav.commissions": "Commissions",
    "admin.nav.partnersOverview": "Apercu des partenaires",
    "admin.nav.management": "Gestion",
    "admin.nav.platformManagers": "Gestionnaires de plateforme",
    "admin.nav.settings": "Parametres",
    "admin.nav.systemSettings": "Parametres systeme",
    "admin.nav.messages": "Messages",
    "admin.nav.contactMessages": "Messages de contact",
    "admin.nav.partnersManagement": "Gestion des partenaires",

    // ==================== NEWS MANAGER ====================
    "admin.news.title": "Actualites et articles",
    "admin.news.subtitle": "Gerer les articles et actualites publies sur le site",
    "admin.news.newArticle": "Nouvel article",
    "admin.news.editArticle": "Modifier l'article",
    "admin.news.all": "Tous",
    "admin.news.draft": "Brouillon",
    "admin.news.published": "Publie",
    "admin.news.scheduled": "Programme",
    "admin.news.search": "Rechercher...",
    "admin.news.noArticles": "Aucun article pour le moment",
    "admin.news.views": "vues",
    "admin.news.titleRequired": "Le titre est obligatoire",
    "admin.news.saving": "Enregistrement...",
    "admin.news.create": "Creer",
    "admin.news.update": "Mettre a jour",
    "admin.news.cancel": "Annuler",
    "admin.news.showSeo": "Afficher les champs SEO",
    "admin.news.hideSeo": "Masquer les champs SEO",
    "admin.news.category": "Categorie",
    "admin.news.tags": "Tags (separes par des virgules)",
    "admin.news.status": "Statut",
    "admin.news.scheduledDate": "Date de publication programmee",
    "admin.news.featuredImage": "URL de l'image principale",
    "admin.news.articleCreated": "Article cree",
    "admin.news.articleUpdated": "Article mis a jour",
    "admin.news.articleDeleted": "Article supprime",
    "admin.news.statusUpdated": "Statut de l'article mis a jour",
    "admin.news.confirmDelete": "Etes-vous sur de vouloir supprimer cet article ?",
    "welcomeBack": "Bon retour",
    "totalRequests": "Total des demandes",
    "pendingRequests": "Demandes en attente",
    "totalEarnings": "Revenus totaux",
    "addCompany": "Ajouter une entreprise",
    "newRequest": "Nouvelle demande",
    "recentRequests": "Demandes récentes",
    "viewAll": "Voir tout",
    "noRequestsYet": "Aucune demande",
    "createFirstRequest": "Créer une demande",
    "getStarted": "Commencer",
    "addFirstCompany": "Ajouter votre première entreprise",
    "runAiDiagnostic": "Lancer un diagnostic IA",
    "submitFirstRequest": "Soumettre une demande",
    "earnFirstCommission": "Gagner une commission",
    "manageYourCompanies": "Gérez vos entreprises clientes",
    "searchCompanies": "Rechercher des entreprises...",
    "allIndustries": "Tous les secteurs",
    "allStatuses": "Tous les statuts",
    "active": "Actif",
    "inactive": "Inactif",
    "noCompaniesYet": "Aucune entreprise",
    "addFirstCompanyDescription": "Ajoutez votre première entreprise cliente",
    "companyName": "Nom de l'entreprise",
    "enterCompanyName": "Entrez le nom de l'entreprise",
    "industry": "Secteur",
    "selectIndustry": "Sélectionnez un secteur",
    "companySize": "Taille de l'entreprise",
    "selectSize": "Sélectionnez la taille",
    "contactName": "Nom du contact",
    "enterContactName": "Entrez le nom du contact",
    "phone": "Téléphone",
    "email": "Email",
    "website": "Site web",
    "city": "Ville",
    "enterCity": "Entrez la ville",
    "notes": "Notes",
    "additionalNotes": "Notes supplémentaires...",
    "cancel": "Annuler",
    "save": "Enregistrer",
    "saving": "Enregistrement...",
    "editCompany": "Modifier l'entreprise",
    "deleteCompany": "Supprimer l'entreprise",
    "deleteCompanyConfirmation": "Êtes-vous sûr de vouloir supprimer cette entreprise ?",
    "delete": "Supprimer",
    "companyNameRequired": "Le nom de l'entreprise est requis",
    "companyAdded": "Entreprise ajoutée",
    "companyUpdated": "Entreprise mise à jour",
    "companyDeleted": "Entreprise supprimée",
    "errorFetchingData": "Erreur de chargement",
    "errorSaving": "Erreur d'enregistrement",
    "errorDeleting": "Erreur de suppression",
    "diagnostics": "Diagnostics",
    "serviceRequests": "Demandes de service",
    "manageServiceRequests": "Gérez vos demandes de services",
    "searchRequests": "Rechercher des demandes...",
    "draft": "Brouillon",
    "submitted": "Soumis",
    "inReview": "En révision",
    "approved": "Approuvé",
    "inProgress": "En cours",
    "completed": "Terminé",
    "rejected": "Rejeté",
    "newServiceRequest": "Nouvelle demande de service",
    "company": "Entreprise",
    "selectCompany": "Sélectionnez une entreprise",
    "service": "Service",
    "selectService": "Sélectionnez un service",
    "requestTitle": "Titre de la demande",
    "enterRequestTitle": "Entrez le titre",
    "description": "Description",
    "describeYourNeeds": "Décrivez vos besoins...",
    "priority": "Priorité",
    "saveAsDraft": "Sauvegarder comme brouillon",
    "submit": "Soumettre",
    "titleRequired": "Le titre est requis",
    "requestSubmitted": "Demande soumise",
    "requestSaved": "Demande sauvegardée",
    "addCompanyFirst": "Ajoutez d'abord une entreprise",
    "createFirstRequestDescription": "Créez votre première demande de service",
    "adminNotes": "Notes admin",
    "errorSubmitting": "Erreur de soumission",
    "aiReadinessDiagnostic": "Diagnostic de maturité IA",
    "selectCompanyForDiagnostic": "Sélectionnez une entreprise",
    "noCompaniesForDiagnostic": "Ajoutez d'abord des entreprises",
    "chooseCompany": "Choisir une entreprise",
    "step": "Étape",
    "complete": "complété",
    "previous": "Précédent",
    "next": "Suivant",
    "showResults": "Voir les résultats",
    "calculating": "Calcul en cours...",
    "diagnosticCompleted": "Diagnostic terminé",
    "diagnosticResults": "Résultats du diagnostic",
    "aiReadinessScore": "Score de maturité IA",
    "recommendations": "Recommandations",
    "startNewDiagnostic": "Nouveau diagnostic",
    "requestService": "Demander un service",
    "availableServices": "Services disponibles",
    "browseAndRequestServices": "Parcourez et demandez des services",
    "allCategories": "Toutes les catégories",
    "commission": "Commission",
    "request": "Demander",
    "addCompanyToRequestServices": "Ajoutez une entreprise pour demander des services",
    "additionalDetails": "Détails supplémentaires",
    "submitRequest": "Soumettre la demande",
    "submitting": "Soumission...",
    "earningsAndCommissions": "Revenus et commissions",
    "trackYourEarnings": "Suivez vos revenus",
    "paidAmount": "Montant payé",
    "pendingAmount": "Montant en attente",
    "totalCommissions": "Total des commissions",
    "commissionsHistory": "Historique des commissions",
    "noCommissionsYet": "Aucune commission",
    "commissionsWillAppear": "Vos commissions apparaîtront ici",
    "commissionRate": "Taux de commission",
    "amount": "Montant",
    "status": "Statut",
    "date": "Date",
    "paid": "Payé",
    "pending": "En attente",
    "paymentInfo": "Informations de paiement",
    "paymentInfoDescription": "Les commissions sont traitées mensuellement.",
    "manageYourAccount": "Gérez votre compte",
    "profileInformation": "Informations du profil",
    "fullName": "Nom complet",
    "enterFullName": "Entrez votre nom",
    "phoneCannotBeChanged": "Le téléphone ne peut pas être modifié",
    "saveChanges": "Enregistrer les modifications",
    "profileUpdated": "Profil mis à jour",
    "security": "Sécurité",
    "pinCode": "Code PIN",
    "pinCodeDescription": "Votre PIN de connexion",
    "changePin": "Changer le PIN",
    "contactAdminToChangePin": "Contactez l'admin pour changer votre PIN",
    "accountStatus": "Statut du compte",
    "partnerAccount": "Compte Partenaire",
    
    // ==================== ADMIN SERVICES MANAGEMENT ====================
    "servicesManagement": "Gestion des Services",
    "servicesManagementDesc": "Gérer les services disponibles pour les partenaires",
    "addService": "Ajouter un Service",
    "editService": "Modifier le Service",
    "serviceName": "Nom du Service",
    "serviceDescription": "Description du Service",
    "category": "Catégorie",
    "icon": "Icône",
    "basePrice": "Prix de Base",
    "serviceActive": "Service Actif",
    "totalServices": "Total des Services",
    "activeServices": "Services Actifs",
    "inactiveServices": "Services Inactifs",
    "noServices": "Aucun service pour l'instant",
    "addFirstService": "Ajouter le Premier Service",
    "serviceAdded": "Service ajouté avec succès",
    "serviceUpdated": "Service mis à jour avec succès",
    "serviceDeleted": "Service supprimé avec succès",
    "deleteServiceConfirm": "Êtes-vous sûr de vouloir supprimer ce service? Cette action est irréversible.",
    "confirmDelete": "Confirmer la Suppression",
    "errorOccurred": "Une erreur s'est produite",
    "nameRequired": "Le nom est requis",
    "add": "Ajouter",
    "automation": "Automatisation",
    "aiAgents": "Agents IA",
    "integrations": "Intégrations",
    "consulting": "Consulting",
    "analytics": "Analytique",
    "searchServices": "Rechercher des services...",
    "showingResults": "Affichage de {count} sur {total} services",
    "noResultsFound": "Aucun service ne correspond à vos filtres",
    "clearFilters": "Effacer les Filtres",

    // ==================== GROWTH REFLECTION ====================
    "growth.reflection.title": "Un moment de réflexion",
    "growth.reflection.subtitle": "Avant de débloquer le niveau suivant, prenez un court moment pour réfléchir à votre parcours.",
    "growth.reflection.question": "Quelle idée a changé votre compréhension aujourd'hui ?",
    "growth.reflection.placeholder": "Partagez ce qui vous a marqué...",
    "growth.reflection.moodQuestion": "Comment vous sentez-vous maintenant ?",
    "growth.reflection.mood.confident": "Confiant",
    "growth.reflection.mood.motivated": "Motivé",
    "growth.reflection.mood.proud": "Fier",
    "growth.reflection.mood.curious": "Curieux",
    "growth.reflection.mood.unsure": "Incertain",
    "growth.reflection.growthQuestion": "Combien avez-vous évolué depuis le début de ce niveau ?",
    "growth.reflection.growthLow": "Un peu",
    "growth.reflection.growthHigh": "Considérablement",
    "growth.reflection.optional": "optionnel",
    "growth.reflection.submit": "Enregistrer et continuer",

    // ==================== GROWTH EVOLUTION (Phase 2) ====================
    "growth.evolution.title": "Votre Évolution Personnelle",
    "growth.evolution.subtitle": "Un regard privé sur le chemin parcouru",
    "growth.evolution.contrast": "Contraste de Réflexion",
    "growth.evolution.then": "Avant",
    "growth.evolution.now": "Maintenant",
    "growth.evolution.moodTimeline": "Évolution de l'Humeur",
    "growth.evolution.growthCurve": "Courbe de Croissance",

    // ==================== COMMITMENT INDEX ====================
    "commitment.title": "Indice de Fiabilité",
    "commitment.subtext": "La constance construit la confiance professionnelle.",
    "commitment.message.high": "Votre fiabilité est exceptionnellement forte",
    "commitment.message.medium": "Vous construisez la confiance pas à pas",
    "commitment.message.low": "Continuez, chaque action compte",
    "commitment.message.start": "Commencez votre parcours vers la constance",

    // ==================== PROMPTS PAGE ====================
    "prompts.badge": "Bibliothèque de Prompts IA",
    "prompts.title": "Découvrez des",
    "prompts.titleHighlight": "Prompts IA Puissants",
    "prompts.subtitle": "Parcourez des prompts sélectionnés pour les modèles IA de texte, image, vidéo et audio. Copiez, votez et exécutez instantanément.",
    "prompts.search": "Rechercher",
    "prompts.searchPlaceholder": "Rechercher des prompts...",
    "prompts.promptType": "Type de Prompt",
    "prompts.category": "Catégorie",
    "prompts.allCategories": "Toutes les Catégories",
    "prompts.sortBy": "Trier par",
    "prompts.resetFilters": "Réinitialiser les Filtres",
    "prompts.loading": "Chargement...",
    "prompts.found": "{count} prompt(s) trouvé(s)",
    "prompts.noResults": "Aucun prompt trouvé",
    "prompts.noResultsHint": "Essayez d'ajuster vos filtres",
    "prompts.trending": "Tendance",
    "prompts.view": "Voir",
    "prompts.copy": "Copier",
    "prompts.copied": "Prompt copié dans le presse-papiers !",
    "prompts.by": "par",
    "prompts.all": "Tous",
    "prompts.type.text": "Texte",
    "prompts.type.image": "Image",
    "prompts.type.video": "Vidéo",
    "prompts.type.audio": "Audio",
    "prompts.cat.marketing": "Marketing",
    "prompts.cat.automation": "Automatisation",
    "prompts.cat.web_development": "Développement Web",
    "prompts.cat.education": "Éducation",
    "prompts.cat.data_science": "Science des Données",
    "prompts.cat.business": "Business",
    "prompts.cat.ai_agents": "Agents IA",
    "prompts.cat.other": "Autre",
    "prompts.sort.trending": "🔥 Tendance",
    "prompts.sort.popular": "👍 Plus Populaires",
    "prompts.sort.copies": "📋 Plus Copiés",
    "prompts.sort.recent": "🕐 Plus Récents",
    "prompts.requireAuth": "Vous devez vous connecter pour utiliser ce prompt.",
    "prompts.signIn": "Connexion",

    // ==================== PROMPT DETAIL PAGE ====================
    "prompts.detail.notFound": "Prompt Introuvable",
    "prompts.detail.notFoundDesc": "Ce prompt n'existe pas ou a été supprimé.",
    "prompts.detail.browseAll": "Parcourir Tous les Prompts",
    "prompts.detail.backToLibrary": "Retour à la Bibliothèque",
    "prompts.detail.upvotes": "Votes positifs",
    "prompts.detail.copies": "Copies",
    "prompts.detail.views": "Vues",
    "prompts.detail.upvote": "Voter",
    "prompts.detail.upvoted": "Voté",
    "prompts.detail.execute": "Exécuter",
    "prompts.detail.content": "Contenu du Prompt",
    "prompts.detail.readyTitle": "Prêt à utiliser ce prompt ?",
    "prompts.detail.readyDesc": "Copiez-le dans votre presse-papiers ou exécutez-le directement dans un chat IA.",
    "prompts.detail.copyPrompt": "Copier le Prompt",
    "prompts.detail.executePrompt": "Exécuter le Prompt",
    "prompts.detail.copiedAndOpening": "Prompt copié et ouverture du chat IA !",
  },
  
  ar: {
    // ==================== NAVIGATION ====================
    "nav.about": "ما هو SYNRA",
    "nav.services": "الخدمات",
    "nav.training": "التدريب",
    "nav.prompts": "الأوامر",
    "nav.partners": "الشركاء",
    "nav.signin": "تسجيل الدخول",
    "nav.getstarted": "ابدأ الآن",
    
    // ==================== LANGUAGE SWITCHER ====================
    "lang.change": "تغيير اللغة",
    "lang.switchTo": "التبديل إلى",
    "lang.en": "الإنجليزية",
    "lang.fr": "الفرنسية",
    "lang.ar": "العربية",
    
    // ==================== COMMON ====================
    "common.loading": "جارٍ التحميل...",
    "common.error": "خطأ",
    "common.success": "نجاح",
    "common.save": "حفظ",
    "common.cancel": "إلغاء",
    "common.submit": "إرسال",
    "common.close": "إغلاق",
    "common.back": "رجوع",
    "common.next": "التالي",
    "common.continue": "متابعة",
    "common.learnMore": "اعرف المزيد",
    "common.viewAll": "عرض الكل",
    "common.comingSoon": "قريباً",
    "common.backToHome": "العودة للرئيسية",
    "common.returnToHome": "العودة للرئيسية",
    "common.questions": "أسئلة؟",
    "common.contactUs": "تواصل معنا على",

    // ==================== HERO SECTION ====================
    "hero.badge": "منصة الذكاء الاصطناعي والأتمتة",
    "hero.title": "حوّل أعمالك مع",
    "hero.titleHighlight": "الذكاء الاصطناعي العملي",
    "hero.subtitle": "SYNRA تمكّن الشركات والشباب المغاربة من إتقان الأتمتة والذكاء الاصطناعي — بطريقة منظمة وآمنة وموجهة نحو النتائج.",
    "hero.cta.partner": "انضم كشريك",
    "hero.cta.youth": "برنامج الشباب",
    "hero.stat.partners": "شركة شريكة",
    "hero.stat.automations": "أتمتة منفذة",
    "hero.stat.youth": "شاب مُدرَّب",

    // ==================== ABOUT SECTION ====================
    "about.label": "عن SYNRA",
    "about.title": "الذكاء الاصطناعي والأتمتة،",
    "about.titleHighlight": "بشكل عملي",
    "about.description1": "SYNRA ليست مجرد منصة برمجية — إنها منظومة ذكاء اصطناعي مصممة للسوق المغربي. نربط الشركات الباحثة عن التحول الرقمي بشركاء مؤهلين ومواهب مدربة.",
    "about.description2": "مهمتنا هي جعل الذكاء الاصطناعي والأتمتة متاحين ومفهومين وقابلين للتطبيق. سواء كنت شركة تسعى لتحسين العمليات، أو استشاري يوسع خدماته، أو شاب محترف يبني مهارات المستقبل — SYNRA هي منصتك.",
    "about.value.trust.title": "الثقة والأمان",
    "about.value.trust.desc": "أمان على مستوى المؤسسات مع عمليات شفافة. بياناتك وعملياتك محمية.",
    "about.value.results.title": "نتائج عملية",
    "about.value.results.desc": "لا ضجيج، فقط نتائج قابلة للقياس. نركز على التطبيقات التي تحقق عائد حقيقي.",
    "about.value.knowledge.title": "نقل المعرفة",
    "about.value.knowledge.desc": "لا نبني فقط — نعلّم. فريقك يكتسب المهارات لصيانة وتوسيع الحلول.",
    "about.value.ecosystem.title": "نهج المنظومة",
    "about.value.ecosystem.desc": "ربط الشركات والشركاء والمواهب في منصة موحدة للنمو المستدام.",

    // ==================== SERVICES SECTION ====================
    "services.label": "خدماتنا",
    "services.title": "حلول ذكاء اصطناعي عملية لـ",
    "services.titleHighlight": "احتياجات الأعمال الحقيقية",
    "services.subtitle": "نقدم حلول الأتمتة والذكاء الاصطناعي التي تحل مشاكل حقيقية — بدون مصطلحات، فقط نتائج.",
    "services.workflow.title": "أتمتة سير العمل",
    "services.workflow.desc": "تخلص من المهام المتكررة وبسّط العمليات مع حلول أتمتة مخصصة. من سير العمل البسيط إلى التكاملات المعقدة.",
    "services.workflow.f1": "رسم خرائط العمليات",
    "services.workflow.f2": "تكامل n8n و Zapier",
    "services.workflow.f3": "اتصالات API",
    "services.workflow.f4": "مشغلات مخصصة",
    "services.ai.title": "وكلاء الذكاء الاصطناعي",
    "services.ai.desc": "انشر وكلاء ذكية تفهم سياق عملك. دعم العملاء، تحليل البيانات، والمساعدة في اتخاذ القرار.",
    "services.ai.f1": "مساعدون ذكاء اصطناعي مخصصون",
    "services.ai.f2": "تكامل معالجة اللغة الطبيعية",
    "services.ai.f3": "دعم متعدد القنوات",
    "services.ai.f4": "التعلم المستمر",
    "services.optimization.title": "تحسين الأنظمة",
    "services.optimization.desc": "قيّم وحلل وحسّن بنيتك التقنية الحالية. حدد الاختناقات وطبق التحسينات.",
    "services.optimization.f1": "تدقيق البنية التقنية",
    "services.optimization.f2": "تحليل الأداء",
    "services.optimization.f3": "مراجعة التكامل",
    "services.optimization.f4": "تخطيط التوسع",

    // ==================== TRAINING SECTION ====================
    "training.label": "برنامج تدريب الشباب",
    "training.title": "ابنِ مهارات حقيقية،",
    "training.titleHighlight": "ليس مجرد نظريات",
    "training.subtitle": "برنامجنا التدريبي مصمم لإعداد محترفين جاهزين للعمل. تعلم بالممارسة — مع الإرشاد والمشاريع ومسارات مهنية واضحة.",
    "training.path.title": "مسار التعلم",
    "training.level.beginner": "مبتدئ",
    "training.level.intermediate": "متوسط",
    "training.level.advanced": "متقدم",
    "training.level.expert": "خبير",
    "training.course.fundamentals": "أساسيات الأتمتة",
    "training.course.n8n": "n8n وسير العمل المتقدم",
    "training.course.agents": "تطوير وكلاء الذكاء الاصطناعي",
    "training.course.projects": "مشاريع واقعية",
    "training.duration.weeks": "أسابيع",
    "training.duration.ongoing": "مستمر",
    "training.skill.workflow": "منطق سير العمل",
    "training.skill.integrations": "التكاملات الأساسية",
    "training.skill.nocode": "أدوات بدون كود",
    "training.skill.n8n": "إتقان n8n",
    "training.skill.api": "تكامل API",
    "training.skill.errors": "معالجة الأخطاء",
    "training.skill.llm": "تكامل النماذج اللغوية",
    "training.skill.agents": "وكلاء مخصصون",
    "training.skill.deployment": "النشر الإنتاجي",
    "training.skill.client": "مشاريع العملاء",
    "training.skill.team": "التعاون الجماعي",
    "training.skill.industry": "حلول قطاعية",
    "training.benefit.curriculum.title": "منهج منظم",
    "training.benefit.curriculum.desc": "تقدم واضح من الأساسيات إلى المفاهيم المتقدمة",
    "training.benefit.projects.title": "مشاريع حقيقية",
    "training.benefit.projects.desc": "اعمل على مشاريع شركات فعلية، ليس مجرد تمارين",
    "training.benefit.certification.title": "شهادات المهارات",
    "training.benefit.certification.desc": "احصل على اعتمادات معترف بها مع تقدمك",
    "training.cta.title": "مستعد لبدء رحلتك؟",
    "training.cta.desc": "انضم لبرنامج الشباب واكتسب المهارات التي تبحث عنها الشركات بنشاط. أماكن محدودة لكل دفعة.",
    "training.cta.button": "قدّم الآن",

    // ==================== PARTNERS SECTION ====================
    "partners.label": "برنامج الشركاء",
    "partners.title": "طوّر أعمالك مع",
    "partners.titleHighlight": "SYNRA",
    "partners.subtitle": "انضم لشبكة شركائنا وقدم خدمات ذكاء اصطناعي وأتمتة على مستوى المؤسسات لعملائك. نحن نوفر التكنولوجيا، وأنت توفر العلاقة.",
    "partners.ideal.title": "الشركاء المثاليون",
    "partners.type.agencies": "الوكالات",
    "partners.type.agencies.desc": "وكالات التسويق الرقمي والويب التي تتوسع في الأتمتة",
    "partners.type.consultants": "الاستشاريون",
    "partners.type.consultants.desc": "استشاريو الأعمال الذين يضيفون الذكاء الاصطناعي لخدماتهم",
    "partners.type.it": "شركات تكنولوجيا المعلومات",
    "partners.type.it.desc": "شركات التكنولوجيا التي تبحث عن حلول أتمتة مجربة",
    "partners.cta": "كن شريكاً",
    "partners.benefit.whitelabel.title": "حلول العلامة البيضاء",
    "partners.benefit.whitelabel.desc": "بِع خدمات SYNRA تحت علامتك التجارية. بوابة شركاء كاملة لإدارة العملاء.",
    "partners.benefit.revenue.title": "مشاركة الإيرادات",
    "partners.benefit.revenue.desc": "هيكل عمولات تنافسي. تتبع أرباحك ومدفوعاتك في الوقت الفعلي.",
    "partners.benefit.sales.title": "دعم المبيعات",
    "partners.benefit.sales.desc": "الوصول إلى العروض التقديمية والمقترحات والقوالب. نساعدك على إتمام الصفقات.",
    "partners.benefit.dashboard.title": "لوحة تحكم العملاء",
    "partners.benefit.dashboard.desc": "أدِر شركاتك، تتبع حالة الأتمتة، وراقب النتائج.",

    // ==================== TESTIMONIALS ====================
    "testimonials.badge": "قصص نجاح",
    "testimonials.title": "ماذا يقول خريجونا",
    "testimonials.subtitle": "نتائج حقيقية من أشخاص حقيقيين غيّروا مسارهم المهني مع SYNRA.",
    "testimonials.1.name": "ياسين العمراني",
    "testimonials.1.sector": "التسويق الرقمي — الدار البيضاء",
    "testimonials.1.quote": "بفضل SYNRA، أتمتّ 80% من تقارير وكالتي. تضاعفت إنتاجيتنا في 3 أشهر فقط.",
    "testimonials.2.name": "فاطمة الزهراء بناني",
    "testimonials.2.sector": "التجارة الإلكترونية — الرباط",
    "testimonials.2.quote": "منحني تدريب الذكاء الاصطناعي المهارات لبناء روبوتات محادثة لمتجري الإلكتروني. زاد رضا العملاء بنسبة 45%.",
    "testimonials.3.name": "عمر التازي",
    "testimonials.3.sector": "تكنولوجيا التعليم — مراكش",
    "testimonials.3.quote": "ساعدني برنامج SYNRA على الانتقال من التعليم التقليدي إلى التعليم المدعوم بالذكاء الاصطناعي. أنا الآن أدرّب معلمين آخرين.",

    // ==================== CTA SECTION ====================
    "cta.badge": "ابدأ رحلتك في الذكاء الاصطناعي",
    "cta.title": "مستعد لتحويل",
    "cta.titleHighlight": "أعمالك؟",
    "cta.subtitle": "سواء كنت شركة تبحث عن الأتمتة، أو شريك يتطلع للتوسع، أو موهبة شابة تبني مهاراتها — SYNRA هي منصتك.",
    "cta.partner": "انضم كشريك",
    "cta.training": "قدّم للتدريب",

    // ==================== FOOTER ====================
    "footer.tagline": "تمكين الشركات والشباب المغاربة بحلول عملية للذكاء الاصطناعي والأتمتة.",
    "footer.platform": "المنصة",
    "footer.platform.services": "الخدمات",
    "footer.platform.training": "التدريب",
    "footer.platform.partners": "الشركاء",
    "footer.platform.pricing": "الأسعار",
    "footer.company": "الشركة",
    "footer.company.about": "عن الشركة",
    "footer.company.careers": "الوظائف",
    "footer.company.blog": "المدونة",
    "footer.company.contact": "اتصل بنا",
    "footer.legal": "قانوني",
    "footer.legal.privacy": "سياسة الخصوصية",
    "footer.legal.terms": "شروط الخدمة",
    "footer.legal.cookies": "سياسة ملفات تعريف الارتباط",
    "footer.copyright": "© {year} SYNRA. جميع الحقوق محفوظة.",
    "footer.madeIn": "صُنع بدقة في المغرب",

    // ==================== AUTH PAGE ====================
    "auth.welcome.title": "مرحباً بك في",
    "auth.welcome.highlight": "رحلتك في الذكاء الاصطناعي",
    "auth.welcome.subtitle": "انضم إلى منظومة الذكاء الاصطناعي والأتمتة الرائدة في المغرب. سواء كنت شريكاً تجارياً أو موهبة طموحة، SYNRA هي منصتك للنمو.",
    "auth.card.partners.title": "الشركاء",
    "auth.card.partners.desc": "وسّع خدماتك مع حلول الذكاء الاصطناعي والأتمتة",
    "auth.card.youth.title": "الشباب",
    "auth.card.youth.desc": "ابنِ مهارات حقيقية من خلال التدريب العملي",
    "auth.tab.signin": "تسجيل الدخول",
    "auth.tab.partner": "شريك",
    "auth.tab.youth": "شباب",
    "auth.signin.title": "مرحباً بعودتك",
    "auth.signin.subtitle": "سجّل الدخول برقم هاتفك ورمز PIN",
    "auth.partner.title": "كن شريكاً",
    "auth.partner.subtitle": "انضم لشبكتنا وقدم خدمات الذكاء الاصطناعي لعملائك",
    "auth.youth.title": "انضم لبرنامج التدريب",
    "auth.youth.subtitle": "ابدأ رحلتك لتصبح محترفاً في الذكاء الاصطناعي",
    "auth.backToHome": "العودة للرئيسية",

    // ==================== 404 PAGE ====================
    "notfound.title": "404",
    "notfound.message": "عفواً! الصفحة غير موجودة",
    "notfound.link": "العودة للرئيسية",

    // ==================== YOUTH SIDEBAR ====================
    "youth.nav.main": "الرئيسي",
    "youth.nav.learning": "التعلم",
    "youth.nav.progress": "التقدم والإنجازات",
    "youth.nav.resources": "الموارد والنمو",
    "youth.home": "الرئيسية",
    "youth.classroom": "الفصل الدراسي",
    "youth.lessons": "الدروس",
    "youth.paths": "مسارات التعلم",
    "youth.quiz": "اختبار",
    "youth.progress": "التقدم",
    "youth.certificates": "الشهادات",
    "youth.skills": "المهارات",
    "youth.resources": "الموارد",
    "youth.practicePosts": "منشورات التطبيق",
    "youth.projects": "المشاريع",
    "youth.opportunities": "الفرص",
    "youth.meet": "لقاءات",
    "youth.chat": "دردشة المجتمع",
     "youth.tasks": "المهام",
     "youth.signout": "تسجيل الخروج",

    // ==================== YOUTH HOME ====================
    "youth.home.title": "مرحباً",
    "youth.home.subtitle": "رحلتك التدريبية في الذكاء الاصطناعي تبدأ هنا",
    "youth.home.platform": "منصة SYNRA",
    "youth.home.greeting": "مرحباً بعودتك، {name}!",
    "youth.home.description": "أتقن مهارات الذكاء الاصطناعي والأتمتة من خلال تدريب منظم ومهني. ابنِ خبرة عملية وافتح فرصاً جديدة.",
    "youth.home.cta.classroom": "الذهاب للفصل",
    "youth.home.cta.paths": "عرض مسارات التعلم",
    "youth.home.card.learning.title": "تعلم منظم",
    "youth.home.card.learning.desc": "تقدم عبر مستويات مصممة بعناية، من المبتدئ إلى المتقدم، مع دروس فيديو وممارسة عملية.",
    "youth.home.card.projects.title": "مشاريع واقعية",
    "youth.home.card.projects.desc": "طبق معرفتك على مشاريع حقيقية وابنِ ملفاً يعرض مهاراتك في الذكاء الاصطناعي والأتمتة.",
    "youth.home.card.career.title": "فرص وظيفية",
    "youth.home.card.career.desc": "احصل على شهادات، افتح فرصاً، وتواصل مع شركات تبحث عن محترفين في الذكاء الاصطناعي.",
    "youth.home.coming.label": "قريباً",
    "youth.home.coming.title": "ميزات ومحتوى جديد",
    "youth.home.coming.desc": "نضيف باستمرار وحدات تدريب وفرص وميزات جديدة لمساعدتك على التقدم. ترقب!",

    // ==================== YOUTH CLASSROOM ====================
    "youth.classroom.title": "الفصل الدراسي",
    "youth.classroom.subtitle": "برامجك المسجلة وتقدمك التعليمي",
    "youth.classroom.loading": "جارٍ تحميل برامجك المسجلة...",
    "youth.classroom.stats.total": "إجمالي المستويات",
    "youth.classroom.stats.inProgress": "قيد التقدم",
    "youth.classroom.stats.completed": "مكتمل",
    "youth.classroom.empty.title": "لا توجد برامج متاحة بعد",
    "youth.classroom.empty.desc": "محتوى التدريب قيد الإعداد. عُد قريباً لبدء رحلتك التعليمية.",
    "youth.classroom.levels.title": "مستويات التعلم الخاصة بك",
    "youth.classroom.viewPaths": "عرض جميع المسارات",
    "youth.classroom.lessons": "دروس",
    "youth.classroom.min": "دقيقة",
    "youth.classroom.complete": "مكتمل",
    "youth.classroom.back": "العودة للمستويات",
    "youth.classroom.yourProgress": "تقدمك",
    "youth.classroom.contentPreparing": "المحتوى قيد الإعداد",
    "youth.classroom.checkBack": "عُد قريباً.",
    "youth.classroom.lessonsTitle": "الدروس",
    "youth.classroom.allComplete.title": "اكتملت جميع الدروس!",
    "youth.classroom.allComplete.desc": "مستعد لاختبار معرفتك؟",
    "youth.classroom.takeQuiz": "ابدأ الاختبار",
    "youth.classroom.selectLesson": "اختر درساً لمشاهدته",
    "youth.classroom.chooseLesson": "اختر درساً من القائمة للبدء",
    "youth.classroom.markComplete": "تحديد كمكتمل",
    "youth.classroom.completed": "مكتمل",
    "youth.classroom.watchToComplete": "شاهد الفيديو للإكمال",
    "youth.classroom.replay": "إعادة التشغيل",
    "youth.classroom.requiredWatch": "وقت المشاهدة المطلوب",
    "youth.classroom.seekBlocked": "لا يسمح بالتقديم السريع",
    "youth.classroom.noSkip": "بدون تخطي",
    "youth.classroom.watchedTime": "إجمالي المشاهدة",
    "youth.classroom.unlocked": "مفتوح",
    "youth.classroom.remaining": "متبقي",
    
    // ==================== SMART CONTEXT PANEL ====================
    "youth.classroom.contextPanel.title": "سياق التعلم",
    "youth.classroom.contextPanel.currentContext": "السياق الحالي",
    "youth.classroom.contextPanel.upcoming": "قادم",
    "youth.classroom.contextPanel.previous": "سابق",
    "youth.classroom.contextPanel.openLink": "فتح الرابط",
    "youth.classroom.contextPanel.waitingForContext": "سيظهر السياق أثناء المشاهدة",
    "youth.classroom.contextPanel.types.platform": "منصة",
    "youth.classroom.contextPanel.types.tool": "أداة",
    "youth.classroom.contextPanel.types.documentation": "توثيق",
    "youth.classroom.contextPanel.types.note": "ملاحظة",
    "youth.classroom.contextPanel.types.exercise": "تمرين",
    "difficulty.beginner": "مبتدئ",
    "difficulty.intermediate": "متوسط",
    "difficulty.advanced": "متقدم",

    // ==================== EMPTY STATES ====================
    "empty.noData": "لا توجد بيانات متاحة",
    "empty.noLessons": "لا توجد دروس متاحة",
    "empty.noCertificates": "لم تحصل على شهادات بعد",
    "empty.noOpportunities": "لا توجد فرص متاحة",
    "empty.noProjects": "لا توجد مشاريع مُسندة",
    "empty.noTasks": "لا توجد مهام مُسندة",
    "empty.noMeets": "لا توجد اجتماعات مجدولة",
    "empty.noSkills": "لا توجد مهارات مسجلة بعد",
    "empty.noResources": "لا توجد موارد متاحة",

    // ==================== ERRORS ====================
    "error.generic": "حدث خطأ ما",
    "error.notFound": "الصفحة غير موجودة",
    "error.unauthorized": "وصول غير مصرح",
    "error.network": "خطأ في الشبكة. يرجى المحاولة مرة أخرى.",
    "error.loadFailed": "فشل في تحميل البيانات",
    "error.validation": "خطأ في التحقق",
    "error.loginFailed": "فشل تسجيل الدخول",
    "error.invalidCredentials": "رقم الهاتف أو PIN غير صحيح. يرجى المحاولة مرة أخرى.",
    "error.pendingConfirmation": "حسابك في انتظار التأكيد.",
    "error.registrationFailed": "فشل التسجيل",
    "error.alreadyRegistered": "رقم الهاتف هذا مسجل بالفعل. يرجى تسجيل الدخول.",
    "error.profileCreationFailed": "تعذر إنشاء الملف الشخصي. يرجى المحاولة مرة أخرى.",
    "error.userCreationFailed": "تعذر إنشاء حساب المستخدم.",
    "error.unexpected": "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.",

    // ==================== AUTH FORMS ====================
    "auth.form.phone": "رقم الهاتف",
    "auth.form.phone.placeholder": "+212 6XX XXX XXX",
    "auth.form.pin": "PIN (4-6 أرقام)",
    "auth.form.pin.placeholder": "••••••",
    "auth.form.fullName": "الاسم الكامل",
    "auth.form.fullName.placeholder.partner": "أحمد المنصوري",
    "auth.form.fullName.placeholder.youth": "يوسف بنجلون",
    "auth.form.companyName": "اسم الشركة",
    "auth.form.companyName.placeholder": "شركتك ش.م.م",
    "auth.form.domain": "مجال العمل",
    "auth.form.domain.placeholder": "مثال: التسويق، خدمات تكنولوجيا المعلومات",
    "auth.form.email": "البريد الإلكتروني (اختياري)",
    "auth.form.email.placeholder": "ahmed@company.ma",
    "auth.form.city": "المدينة",
    "auth.form.city.placeholder": "الدار البيضاء",
    "auth.form.level": "المستوى الحالي",
    "auth.form.level.beginner": "مبتدئ - بدون خبرة",
    "auth.form.level.intermediate": "متوسط - بعض المعرفة",
    "auth.form.level.advanced": "متقدم - خبرة",
    "auth.form.required": "*",
    "auth.form.signin": "تسجيل الدخول",
    "auth.form.signingIn": "جارٍ تسجيل الدخول...",
    "auth.form.createAccount": "إنشاء حساب",
    "auth.form.creatingAccount": "جارٍ إنشاء الحساب...",
    "auth.form.welcomeBack": "مرحباً بعودتك!",
    "auth.form.loginSuccess": "تم تسجيل الدخول بنجاح.",

    // ==================== PASSWORD CONFIRMATION ====================
    "auth.password.title": "رمز PIN الخاص بك",
    "auth.password.subtitle": "احفظ هذا الرمز بأمان. ستحتاجه لتسجيل الدخول.",
    "auth.password.warning": "لن يتم عرض هذا الرمز مرة أخرى!",
    "auth.password.copied": "تم نسخ رمز PIN!",
    "auth.password.copy": "نسخ PIN",
    "auth.password.confirm": "لقد حفظت رمز PIN",

    // ==================== PENDING APPROVAL ====================
    "auth.pending.title": "تم تقديم الطلب",
    "auth.pending.greeting": "شكراً لك، {name}!",
    "auth.pending.message": "طلبك قيد المراجعة من فريقنا. ستتلقى إشعاراً بمجرد الموافقة على حسابك.",
    "auth.pending.signin": "الذهاب لتسجيل الدخول",

    // ==================== YOUTH LEARNING PATHS ====================
    "youth.paths.title": "مسارات التعلم",
    "youth.paths.subtitle": "برامج تدريب منظمة لبناء خبرتك في الذكاء الاصطناعي والأتمتة",
    "youth.paths.empty.title": "لا توجد مسارات متاحة",
    "youth.paths.empty.desc": "برامج التدريب قيد الإعداد. يرجى العودة لاحقاً.",
    "youth.paths.continue": "متابعة التعلم",
    "youth.paths.available": "المسارات المتاحة",
    "youth.paths.completed": "مكتمل",
    "youth.paths.locked": "مقفل",
    "youth.paths.inProgress": "قيد التقدم",
    "youth.paths.modules": "وحدات",
    "youth.paths.startPath": "بدء المسار",
    "youth.paths.review": "مراجعة",
    "youth.paths.unlockMessage": "أكمل المسارات السابقة للفتح",

    // ==================== YOUTH OPPORTUNITIES ====================
    "youth.opportunities.title": "الفرص",
    "youth.opportunities.subtitle": "وظائف وتدريب وعمل حر متوافق مع مهاراتك",
    "youth.opportunities.empty.title": "لا توجد فرص متاحة",
    "youth.opportunities.empty.desc": "ستظهر فرص جديدة هنا عند توفرها.",
    "youth.opportunities.available": "متاحة لك",
    "youth.opportunities.locked": "أكمل المستويات المطلوبة",
    "youth.opportunities.apply": "تقديم الآن",
    "youth.opportunities.remote": "عن بُعد",
    "youth.opportunities.requiredLevels": "المستويات المطلوبة:",
    "youth.opportunities.type.job": "وظيفة بدوام كامل",
    "youth.opportunities.type.freelance": "عمل حر",
    "youth.opportunities.type.internship": "تدريب",
    "youth.opportunities.type.contract": "عقد",

    // ==================== YOUTH PROJECTS ====================
    "youth.projects.title": "المشاريع",
    "youth.projects.subtitle": "مشاريع واقعية ومحاكاة لبناء معرض أعمالك",
    "youth.projects.empty.title": "لا توجد مشاريع مُسندة",
    "youth.projects.empty.desc": "ستظهر المشاريع هنا عند إسنادها من الإدارة.",
    "youth.projects.active": "المشاريع النشطة",
    "youth.projects.completed": "المشاريع المكتملة",
    "youth.projects.role": "الدور:",
    "youth.projects.started": "بدأ:",
    "youth.projects.status.active": "نشط",
    "youth.projects.status.completed": "مكتمل",
    "youth.projects.type.internal": "داخلي",
    "youth.projects.type.company": "شركة",
    "youth.projects.type.simulation": "محاكاة",
    "youth.projects.view": "عرض المشروع",
    "youth.projects.progress": "التقدم",
    "youth.projects.milestones": "مراحل المشروع",
    "youth.projects.resources": "موارد المشروع",
    "youth.projects.noMilestones": "لا توجد مراحل محددة لهذا المشروع",
    "youth.projects.noResources": "لا توجد موارد متاحة",
    "youth.projects.submit": "تسليم عملك",
    "youth.projects.pending": "تسليمك قيد المراجعة",
    "youth.projects.instructions": "تعليمات التسليم",
    "youth.projects.notes": "وصف العمل",
    "youth.projects.notesPlaceholder": "اوصف عملك...",
    "youth.projects.url": "رابط المشروع",
    "youth.projects.submitBtn": "تسليم المشروع",
    "youth.projects.submitting": "جارٍ التسليم...",
    "youth.projects.submissions": "التسليمات السابقة",
    "youth.projects.feedback": "التعليقات",
    "youth.projects.dueDate": "تاريخ الاستحقاق",
    "youth.projects.deadline": "آخر أجل",
    "youth.projects.daysRemaining": "يوم متبقي",
    "youth.projects.overdue": "تجاوز الموعد",
    "youth.projects.dueToday": "ينتهي اليوم",
    "youth.projects.dueSoon": "ينتهي قريباً",
    "youth.projects.urgentWarning": "متبقي {days} أيام فقط!",
    "youth.projects.notFound": "المشروع غير موجود أو غير مُسند إليك",
    "youth.projects.status.pending_review": "قيد المراجعة",
    "youth.projects.status.approved": "تمت الموافقة",
    "youth.projects.status.needs_revision": "يحتاج مراجعة",
    "youth.projects.status.rejected": "مرفوض",

    // ==================== YOUTH PROGRESS ====================
    "youth.progress.title": "التقدم والتقييم",
    "youth.progress.subtitle": "تتبع رحلة التعلم ومقاييس الأداء",
    "youth.progress.overall": "التقدم الكلي",
    "youth.progress.avgScore": "متوسط الدرجات",
    "youth.progress.currentLevel": "المستوى الحالي",
    "youth.progress.classroomLevels": "مستويات الفصل",
    "youth.progress.learningPaths": "مسارات التعلم",
    "youth.progress.completed": "مكتمل",
    "youth.progress.inProgress": "قيد التقدم",
    "youth.progress.available": "متاح",
    "youth.progress.totalLevels": "إجمالي المستويات",
    "youth.progress.videos": "فيديوهات",
    "youth.progress.quiz": "اختبار",
    "youth.progress.passed": "ناجح",
    "youth.progress.notPassed": "غير ناجح",
    "youth.progress.taskCompletion": "إنجاز المهام",
    "youth.progress.recentEvaluations": "التقييمات الأخيرة",
    "youth.progress.noEvaluations": "لا توجد تقييمات بعد",
    "youth.progress.of": "من",
    "youth.progress.personalDev": "التطوير الشخصي",

    // ==================== YOUTH TASKS ====================
    "youth.tasks.title": "المهام",
    "youth.tasks.subtitle": "عرض المهام المُسندة وتقديم عملك ومتابعة الملاحظات",
    "youth.tasks.empty.title": "لا توجد مهام مُسندة",
    "youth.tasks.empty.desc": "ستظهر المهام هنا عند إسنادها من الإدارة أو المرشد.",
    "youth.tasks.empty.caughtUp": "أنت على اطلاع.",
    "youth.tasks.empty.newAssignments": "ستظهر المهام الجديدة هنا عند إسنادها من المدرّب.",
    "youth.tasks.pending": "قيد الانتظار",
    "youth.tasks.underReview": "قيد المراجعة",
    "youth.tasks.completed": "مكتملة",
    "youth.tasks.submit": "تقديم",
    "youth.tasks.resubmit": "إعادة التقديم",
    "youth.tasks.submitTask": "تقديم المهمة",
    "youth.tasks.submitting": "جارٍ التقديم...",
    "youth.tasks.submissionText": "نص التقديم",
    "youth.tasks.link": "رابط (اختياري)",
    "youth.tasks.due": "الموعد:",
    "youth.tasks.submitted": "تم التقديم:",
    "youth.tasks.feedback": "ملاحظات",
    "youth.tasks.describe": "اوصف عملك أو الصق إجابتك...",
    "youth.tasks.overdue": "متأخر",
    "youth.tasks.completion": "الإنجاز",
    "youth.tasks.reliabilityNote": "يؤثر على المؤشر",
    "youth.tasks.reliabilityWarning": "التقديمات المتأخرة قد تؤثر على مؤشر الموثوقية الخاص بك.",
    "youth.tasks.noTasksFilter": "لا توجد مهام تطابق هذا الفلتر.",
    "youth.tasks.filter.all": "الكل",
    "youth.tasks.filter.pending": "قيد الانتظار",
    "youth.tasks.filter.submitted": "مُقدَّمة",
    "youth.tasks.filter.approved": "موافق عليها",
    "youth.tasks.filter.overdue": "متأخرة",
    "youth.tasks.status.approved": "موافق عليه",
    "youth.tasks.status.submitted": "تم التقديم",
    "youth.tasks.status.needsRevision": "يحتاج مراجعة",
    "youth.tasks.status.pending": "قيد الانتظار",
    "youth.tasks.status.rejected": "مرفوض",
    "youth.tasks.filter.active": "نشطة",
    "youth.tasks.filter.underReview": "قيد المراجعة",
    "youth.tasks.filter.needsRevision": "تحتاج مراجعة",
    "youth.tasks.filter.completed": "مكتملة",
    "youth.tasks.empty.levelUnlock": "أكمل مستوى لفتح المهام التطبيقية.",

    // ==================== YOUTH PRACTICE POSTS ====================
    "youth.practicePosts.title": "منشورات التطبيق",
    "youth.practicePosts.subtitle": "موارد تعلم تفاعلية",
    "youth.practicePosts.platformLinks": "روابط المنصة",
    "youth.practicePosts.steps": "الخطوات",
    "youth.practicePosts.comments": "التعليقات",
    "youth.practicePosts.addComment": "أضف تعليقاً...",
    "youth.practicePosts.noComments": "لا توجد تعليقات بعد. كن أول من يعلق!",
    "youth.practicePosts.noPosts": "لا توجد منشورات تطبيقية متاحة بعد",

    // ==================== YOUTH CHAT ====================
    "youth.chat.title": "مجتمع المنصة",
    "youth.chat.subtitle": "تواصل مع جميع أعضاء المنصة",
    "youth.chat.members": "عضو متصل",
    "youth.chat.emptyGroup": "لا توجد رسائل بعد",
    "youth.chat.emptyGroupHint": "كن أول من يرسل رسالة في المجتمع!",
    "youth.chat.messagePlaceholder": "اكتب رسالتك...",

    // ==================== NOTIFICATIONS ====================
    "notifications": "الإشعارات",
    "markAllRead": "تحديد الكل كمقروء",
    "noNotifications": "لا توجد إشعارات",

    // ==================== YOUTH COMMENTS ====================
    "youth.comments.title": "التعليقات",
    "youth.comments.placeholder": "اكتب تعليقك أو سؤالك هنا...",
    "youth.comments.reply": "رد",
    "youth.comments.delete": "حذف",
    "youth.comments.replyPlaceholder": "اكتب ردك...",
    "youth.comments.empty": "لا توجد تعليقات بعد. كن أول من يعلق!",
    "youth.comments.error": "حدث خطأ",
    "youth.comments.deleteError": "حدث خطأ في الحذف",

    // ==================== YOUTH ROADMAP ====================
    "youth.roadmap.title": "رحلتك التعليمية",
    "youth.roadmap.subtitle": "أكمل جميع المستويات الـ 12 لتصبح خبيراً معتمداً",

    // ==================== YOUTH MEET ====================
    "youth.meet.title": "لقاءات",
    "youth.meet.subtitle": "انضم للجلسات المباشرة مع المدربين والمرشدين",
    "youth.meet.empty.title": "لا توجد جلسات متاحة",
    "youth.meet.empty.desc": "ستظهر الجلسات المباشرة هنا عند جدولتها لمستواك.",
    "youth.meet.upcoming": "الجلسات القادمة",
    "youth.meet.past": "الجلسات السابقة",
    "youth.meet.today": "اليوم",
    "youth.meet.joined": "انضممت",
    "youth.meet.attended": "حضرت",
    "youth.meet.ended": "انتهت",
    "youth.meet.join": "انضم للجلسة",
    "youth.meet.rejoin": "إعادة الانضمام",
    "youth.meet.notAvailable": "غير متاح بعد",
    "youth.meet.attendanceRecorded": "تم تسجيل الحضور",
    "youth.meet.attendanceDesc": "تم تسجيل حضورك لهذه الجلسة.",
    "youth.meet.session": "جلسة",
    "youth.meet.openNewTab": "ستفتح هذه الجلسة في نافذة جديدة. تأكد من جهوزية الكاميرا والميكروفون.",
    "youth.meet.joinLive": "انضم للجلسة المباشرة",
    "youth.meet.opensNewTab": "تفتح في نافذة جديدة",
    "youth.meet.minSession": "دقيقة للجلسة",

    // ==================== YOUTH LESSONS ====================
    "youth.lessons.title": "الدروس",
    "youth.lessons.subtitle": "الوصول لمحتوى التعلم والفيديوهات والموارد",
    "youth.lessons.empty.title": "لا توجد دروس متاحة",
    "youth.lessons.empty.desc": "ابدأ مسار تعلم أولاً للوصول للدروس. الدروس منظمة ضمن برامج تدريب.",
    "youth.lessons.browsePaths": "تصفح مسارات التعلم",
    "youth.lessons.noModules": "لا توجد وحدات",
    "youth.lessons.completed": "مكتمل",
    "youth.lessons.selectLesson": "اختر درساً",
    "youth.lessons.chooseLesson": "اختر درساً من مساراتك النشطة لعرض المحتوى",
    "youth.lessons.markComplete": "تحديد كمكتمل",
    "youth.lessons.resources": "الموارد",
    "youth.lessons.pdfDocument": "مستند PDF",
    "youth.lessons.externalLink": "رابط خارجي",

    // ==================== YOUTH QUIZ ====================
    "youth.quiz.title": "اختبار",
    "youth.quiz.subtitle": "أكمل الاختبارات لفتح المستوى التالي",
    "youth.quiz.empty.title": "لا توجد اختبارات متاحة",
    "youth.quiz.empty.desc": "ستظهر الاختبارات عند نشر مستويات التدريب.",
    "youth.quiz.start": "بدء الاختبار",
    "youth.quiz.back": "العودة للاختبارات",
    "youth.quiz.answerAll": "أجب على جميع الأسئلة لإكمال الاختبار",
    "youth.quiz.noQuestions": "لا توجد أسئلة متاحة",
    "youth.quiz.noQuestionsDesc": "هذا الاختبار لا يحتوي على أسئلة بعد.",
    "youth.quiz.submit": "تقديم الاختبار",
    "youth.quiz.submitting": "جارٍ التقديم...",
    "youth.quiz.passingScore": "درجة النجاح:",
    "youth.quiz.timeLimit": "الحد الزمني:",
    "youth.quiz.attempts": "المحاولات:",
    "youth.quiz.completeVideos": "أكمل جميع الفيديوهات ({percent}% منجز) لفتح هذا الاختبار.",
    "youth.quiz.completed": "مكتمل",
    "youth.quiz.alreadyCompleted": "الاختبار مكتمل بالفعل",
    "youth.quiz.alreadyCompletedDesc": "هذا الاختبار مكتمل. لا يمكنك إعادته.",
    "youth.quiz.passedLocked": "نجحت واُغلق الاختبار!",
    "youth.quiz.passedDesc": "تهانينا! لقد أكملت هذا المستوى بنجاح.",
    "youth.quiz.score": "الدرجة:",
    "youth.quiz.permanentlyLocked": "هذا الاختبار مغلق الآن بشكل دائم. لا يُسمح بإعادة المحاولة.",
    "youth.quiz.failed": "فشل الاختبار",
    "youth.quiz.failedDesc": "لا تستسلم! راجع المحتوى وحاول مرة أخرى.",
    "youth.quiz.retry": "إعادة المحاولة",
    "youth.quiz.result": "نتيجة الاختبار",

    // ==================== YOUTH SKILLS ====================
    "youth.skills.title": "المهارات",
    "youth.skills.subtitle": "مهاراتك المكتسبة ومستويات الإتقان",
    "youth.skills.empty.title": "لا توجد مهارات مكتسبة بعد",
    "youth.skills.empty.desc": "أكمل الدورات والمهام لكسب المهارات والشارات.",
    "youth.skills.summary": "المهارات المكتسبة",
    "youth.skills.acquired": "اكتُسبت:",
    "youth.skills.level.beginner": "مبتدئ",
    "youth.skills.level.elementary": "أساسي",
    "youth.skills.level.intermediate": "متوسط",
    "youth.skills.level.advanced": "متقدم",
    "youth.skills.level.expert": "خبير",

    // ==================== YOUTH CERTIFICATES ====================
    "youth.certificates.title": "الشهادات",
    "youth.certificates.subtitle": "شهاداتك واعتماداتك المكتسبة",
    "youth.certificates.earned": "شهادة",
    "youth.certificates.count": "مكتسبة",

    // ==================== YOUTH RESOURCES ====================
    "youth.resources.title": "مواردي",
    "youth.resources.subtitle": "إدارة أدواتك ومواردك",
    "youth.resources.empty.title": "لا توجد موارد متاحة",
    "youth.resources.empty.desc": "ستظهر الموارد هنا عند توفرها.",
    "youth.resources.complete": "مكتمل",
    "youth.resources.incomplete": "غير مكتمل",
    "youth.resources.missingCritical": "موارد حرجة مفقودة",
    "youth.resources.notFilled": "لم يتم التعبئة",
    "youth.resources.updateSuccess": "تم تحديث الموارد بنجاح",
    "youth.resources.updateError": "خطأ في حفظ الموارد",
    "youth.resources.fillRequired": "يرجى ملء جميع الحقول المطلوبة",
    "youth.resources.yes": "نعم",
    "youth.resources.no": "لا",
    "youth.resources.select": "اختر...",
    "youth.resources.available": "متاح",
    "youth.resources.notAvailable": "غير متاح",
    "youth.resources.required": "مطلوب",
    "youth.resources.notSet": "غير محدد",
    "youth.resources.critical": "حرج",
    "youth.resources.edit": "تعديل",
    "youth.resources.save": "حفظ",
    "youth.resources.cancel": "إلغاء",

    // ==================== PARTNER PORTAL ====================
    "partnerPortal": "بوابة الشريك",
    "partnerPortalDescription": "إدارة شركاتك وخدمات الأتمتة",
    "overview": "نظرة عامة",
    "companies": "الشركات",
    "requests": "الطلبات",
    "documents": "المستندات",
    "aiDiagnostic": "تشخيص AI",
    "services": "الخدمات",
    "earnings": "الأرباح",
    "settings": "الإعدادات",
    "signOut": "تسجيل الخروج",
    
    // ==================== ADMIN MODE SELECTOR ====================
    "admin.modeSelector.welcome": "مرحباً",
    "admin.modeSelector.subtitle": "اختر القسم الذي تريد الوصول إليه",
    "admin.modeSelector.select": "اختيار",
    "admin.modeSelector.partners.title": "إدارة الشركاء",
    "admin.modeSelector.partners.subtitle": "إدارة طلبات الشركاء والعمولات",
    "admin.modeSelector.partners.feature1": "طلبات الخدمات",
    "admin.modeSelector.partners.feature2": "إدارة العمولات",
    "admin.modeSelector.partners.feature3": "نظرة عامة على الشركاء",
    "admin.modeSelector.full.title": "لوحة التحكم الكاملة",
    "admin.modeSelector.full.subtitle": "إدارة المنصة بالكامل",
    "admin.modeSelector.full.feature1": "إدارة المستخدمين",
    "admin.modeSelector.full.feature2": "التدريب والشهادات",
    "admin.modeSelector.full.feature3": "التحليلات والإعدادات",
    "admin.changeSection": "تغيير القسم",
    "admin.mode.partners": "وضع الشركاء",
    "admin.mode.full": "الإدارة الكاملة",
    "admin.signOut": "تسجيل الخروج",
    "admin.modeSelector.stats.requests": "الطلبات",
    "admin.modeSelector.stats.commissions": "العمولات",
    "admin.modeSelector.stats.users": "المستخدمين",
    "admin.modeSelector.stats.submissions": "التسليمات",

    // ==================== ADMIN SIDEBAR NAV ====================
    "admin.nav.dashboard": "لوحة التحكم",
    "admin.nav.overview": "نظرة عامة",
    "admin.nav.users": "المستخدمون",
    "admin.nav.allUsers": "جميع المستخدمين",
    "admin.nav.pendingApprovals": "طلبات الموافقة",
    "admin.nav.training": "التدريب",
    "admin.nav.classroom": "الفصل الدراسي",
    "admin.nav.learningPaths": "مسارات التعلم",
    "admin.nav.meets": "اللقاءات",
    "admin.nav.certificates": "الشهادات",
    "admin.nav.tasks": "المهام",
    "admin.nav.projects": "المشاريع",
    "admin.nav.opportunities": "الفرص",
    "admin.nav.resources": "الموارد",
    "admin.nav.practicePosts": "منشورات التمرين",
    "admin.nav.announcements": "الإعلانات",
    "admin.nav.content": "المحتوى",
    "admin.nav.news": "الأخبار",
    "admin.nav.skills": "المهارات",
    "admin.nav.skillsManager": "إدارة المهارات",
    "admin.nav.analytics": "التحليلات",
    "admin.nav.youthProgress": "تقدم الشباب",
    "admin.nav.videoAnalytics": "تحليلات الفيديو",
    "admin.nav.partners": "الشركاء",
    "admin.nav.services": "الخدمات",
    "admin.nav.serviceOptions": "خيارات الخدمات",
    "admin.nav.serviceRequests": "طلبات الخدمات",
    "admin.nav.commissions": "العمولات",
    "admin.nav.partnersOverview": "نظرة عامة على الشركاء",
    "admin.nav.management": "الإدارة",
    "admin.nav.platformManagers": "مديرو المنصة",
    "admin.nav.settings": "الإعدادات",
    "admin.nav.systemSettings": "إعدادات النظام",
    "admin.nav.messages": "الرسائل",
    "admin.nav.contactMessages": "رسائل التواصل",
    "admin.nav.partnersManagement": "إدارة الشركاء",

    // ==================== NEWS MANAGER ====================
    "admin.news.title": "الأخبار والمقالات",
    "admin.news.subtitle": "إدارة المقالات والأخبار المنشورة على الموقع",
    "admin.news.newArticle": "مقال جديد",
    "admin.news.editArticle": "تعديل المقال",
    "admin.news.all": "الكل",
    "admin.news.draft": "مسودة",
    "admin.news.published": "منشور",
    "admin.news.scheduled": "مجدول",
    "admin.news.search": "بحث...",
    "admin.news.noArticles": "لا توجد مقالات بعد",
    "admin.news.views": "مشاهدة",
    "admin.news.titleRequired": "العنوان مطلوب",
    "admin.news.saving": "جاري الحفظ...",
    "admin.news.create": "إنشاء",
    "admin.news.update": "تحديث",
    "admin.news.cancel": "إلغاء",
    "admin.news.showSeo": "إظهار حقول SEO",
    "admin.news.hideSeo": "إخفاء حقول SEO",
    "admin.news.category": "الفئة",
    "admin.news.tags": "الوسوم (مفصولة بفاصلة)",
    "admin.news.status": "الحالة",
    "admin.news.scheduledDate": "تاريخ النشر المجدول",
    "admin.news.featuredImage": "رابط الصورة الرئيسية",
    "admin.news.articleCreated": "تم إنشاء المقال",
    "admin.news.articleUpdated": "تم تحديث المقال",
    "admin.news.articleDeleted": "تم حذف المقال",
    "admin.news.statusUpdated": "تم تحديث حالة المقال",
    "admin.news.confirmDelete": "هل أنت متأكد من حذف هذا المقال؟",
    "welcomeBack": "مرحباً بعودتك",
    "totalRequests": "إجمالي الطلبات",
    "pendingRequests": "الطلبات المعلقة",
    "totalEarnings": "إجمالي الأرباح",
    "addCompany": "إضافة شركة",
    "newRequest": "طلب جديد",
    "recentRequests": "الطلبات الأخيرة",
    "viewAll": "عرض الكل",
    "noRequestsYet": "لا توجد طلبات بعد",
    "createFirstRequest": "إنشاء أول طلب",
    "getStarted": "ابدأ الآن",
    "addFirstCompany": "أضف أول شركة",
    "runAiDiagnostic": "تشغيل تشخيص AI",
    "submitFirstRequest": "قدم أول طلب",
    "earnFirstCommission": "احصل على أول عمولة",
    "manageYourCompanies": "إدارة شركاتك العميلة",
    "searchCompanies": "البحث عن شركات...",
    "allIndustries": "جميع القطاعات",
    "allStatuses": "جميع الحالات",
    "active": "نشط",
    "inactive": "غير نشط",
    "noCompaniesYet": "لا توجد شركات بعد",
    "addFirstCompanyDescription": "أضف أول شركة عميلة للبدء",
    "companyName": "اسم الشركة",
    "enterCompanyName": "أدخل اسم الشركة",
    "industry": "القطاع",
    "selectIndustry": "اختر القطاع",
    "companySize": "حجم الشركة",
    "selectSize": "اختر الحجم",
    "contactName": "اسم جهة الاتصال",
    "enterContactName": "أدخل اسم جهة الاتصال",
    "phone": "الهاتف",
    "email": "البريد الإلكتروني",
    "website": "الموقع الإلكتروني",
    "city": "المدينة",
    "enterCity": "أدخل المدينة",
    "notes": "ملاحظات",
    "additionalNotes": "ملاحظات إضافية...",
    "cancel": "إلغاء",
    "save": "حفظ",
    "saving": "جارٍ الحفظ...",
    "editCompany": "تعديل الشركة",
    "deleteCompany": "حذف الشركة",
    "deleteCompanyConfirmation": "هل أنت متأكد من حذف هذه الشركة؟ لا يمكن التراجع عن هذا الإجراء.",
    "delete": "حذف",
    "companyNameRequired": "اسم الشركة مطلوب",
    "companyAdded": "تمت إضافة الشركة بنجاح",
    "companyUpdated": "تم تحديث الشركة بنجاح",
    "companyDeleted": "تم حذف الشركة بنجاح",
    "errorFetchingData": "خطأ في جلب البيانات",
    "errorSaving": "خطأ في الحفظ",
    "errorDeleting": "خطأ في الحذف",
    "diagnostics": "التشخيصات",
    "serviceRequests": "طلبات الخدمات",
    "manageServiceRequests": "إدارة طلبات خدمات الأتمتة والذكاء الاصطناعي",
    "searchRequests": "البحث عن طلبات...",
    "draft": "مسودة",
    "submitted": "تم التقديم",
    "inReview": "قيد المراجعة",
    "approved": "تمت الموافقة",
    "inProgress": "قيد التنفيذ",
    "completed": "مكتمل",
    "rejected": "مرفوض",
    "newServiceRequest": "طلب خدمة جديد",
    "company": "الشركة",
    "selectCompany": "اختر شركة",
    "service": "الخدمة",
    "selectService": "اختر خدمة",
    "requestTitle": "عنوان الطلب",
    "enterRequestTitle": "أدخل عنوان الطلب",
    "description": "الوصف",
    "describeYourNeeds": "صف احتياجاتك...",
    "priority": "الأولوية",
    "saveAsDraft": "حفظ كمسودة",
    "submit": "تقديم",
    "titleRequired": "العنوان مطلوب",
    "requestSubmitted": "تم تقديم الطلب بنجاح",
    "requestSaved": "تم حفظ الطلب كمسودة",
    "addCompanyFirst": "أضف شركة أولاً لإنشاء طلبات الخدمة",
    "createFirstRequestDescription": "أنشئ أول طلب خدمة أتمتة أو ذكاء اصطناعي",
    "adminNotes": "ملاحظات المدير",
    "errorSubmitting": "خطأ في تقديم الطلب",
    "aiReadinessDiagnostic": "تشخيص الجاهزية للذكاء الاصطناعي",
    "selectCompanyForDiagnostic": "اختر شركة لإجراء التشخيص",
    "noCompaniesForDiagnostic": "أضف شركات أولاً لإجراء التشخيص",
    "chooseCompany": "اختر شركة",
    "step": "الخطوة",
    "complete": "مكتمل",
    "previous": "السابق",
    "next": "التالي",
    "showResults": "عرض النتائج",
    "calculating": "جارٍ الحساب...",
    "diagnosticCompleted": "تم إكمال التشخيص بنجاح",
    "diagnosticResults": "نتائج التشخيص",
    "aiReadinessScore": "درجة الجاهزية للذكاء الاصطناعي",
    "recommendations": "التوصيات",
    "startNewDiagnostic": "بدء تشخيص جديد",
    "requestService": "طلب خدمة",
    "availableServices": "الخدمات المتاحة",
    "browseAndRequestServices": "تصفح واطلب خدمات الأتمتة والذكاء الاصطناعي",
    "allCategories": "جميع الفئات",
    "commission": "العمولة",
    "request": "طلب",
    "addCompanyToRequestServices": "أضف شركة لطلب الخدمات",
    "additionalDetails": "تفاصيل إضافية",
    "submitRequest": "تقديم الطلب",
    "submitting": "جارٍ التقديم...",
    "earningsAndCommissions": "الأرباح والعمولات",
    "trackYourEarnings": "تتبع أرباحك من المشاريع المكتملة",
    "paidAmount": "المبلغ المدفوع",
    "pendingAmount": "المبلغ المعلق",
    "totalCommissions": "إجمالي العمولات",
    "commissionsHistory": "سجل العمولات",
    "noCommissionsYet": "لا توجد عمولات بعد",
    "commissionsWillAppear": "ستظهر عمولاتك هنا عند اكتمال المشاريع",
    "commissionRate": "نسبة العمولة",
    "amount": "المبلغ",
    "status": "الحالة",
    "date": "التاريخ",
    "paid": "مدفوع",
    "pending": "معلق",
    "paymentInfo": "معلومات الدفع",
    "paymentInfoDescription": "تتم معالجة العمولات شهرياً. تواصل مع المدير لتفاصيل الدفع.",
    "manageYourAccount": "إدارة إعدادات حسابك",
    "profileInformation": "معلومات الملف الشخصي",
    "fullName": "الاسم الكامل",
    "enterFullName": "أدخل اسمك الكامل",
    "phoneCannotBeChanged": "لا يمكن تغيير رقم الهاتف",
    "saveChanges": "حفظ التغييرات",
    "profileUpdated": "تم تحديث الملف الشخصي بنجاح",
    "security": "الأمان",
    "pinCode": "رمز PIN",
    "pinCodeDescription": "يُستخدم رمز PIN لتسجيل الدخول",
    "changePin": "تغيير PIN",
    "contactAdminToChangePin": "تواصل مع المدير لتغيير رمز PIN",
    "accountStatus": "حالة الحساب",
    "partnerAccount": "حساب شريك",
    
    // ==================== ADMIN SERVICES MANAGEMENT ====================
    "servicesManagement": "إدارة الخدمات",
    "servicesManagementDesc": "إدارة الخدمات المتاحة للشركاء",
    "addService": "إضافة خدمة",
    "editService": "تعديل الخدمة",
    "serviceName": "اسم الخدمة",
    "serviceDescription": "وصف الخدمة",
    "category": "الفئة",
    "icon": "الأيقونة",
    "basePrice": "السعر الأساسي",
    "serviceActive": "الخدمة نشطة",
    "totalServices": "إجمالي الخدمات",
    "activeServices": "الخدمات النشطة",
    "inactiveServices": "الخدمات غير النشطة",
    "noServices": "لا توجد خدمات بعد",
    "addFirstService": "أضف أول خدمة",
    "serviceAdded": "تمت إضافة الخدمة بنجاح",
    "serviceUpdated": "تم تحديث الخدمة بنجاح",
    "serviceDeleted": "تم حذف الخدمة بنجاح",
    "deleteServiceConfirm": "هل أنت متأكد من حذف هذه الخدمة؟ لا يمكن التراجع عن هذا الإجراء.",
    "confirmDelete": "تأكيد الحذف",
    "errorOccurred": "حدث خطأ",
    "nameRequired": "الاسم مطلوب",
    "add": "إضافة",
    "automation": "الأتمتة",
    "aiAgents": "وكلاء الذكاء الاصطناعي",
    "integrations": "التكاملات",
    "consulting": "الاستشارات",
    "analytics": "التحليلات",
    "searchServices": "البحث عن الخدمات...",
    "showingResults": "عرض {count} من {total} خدمة",
    "noResultsFound": "لا توجد خدمات تطابق الفلاتر",
    "clearFilters": "مسح الفلاتر",

    // ==================== GROWTH REFLECTION ====================
    "growth.reflection.title": "لحظة للتأمل",
    "growth.reflection.subtitle": "قبل فتح المستوى التالي، خذ لحظة قصيرة للتأمل في رحلتك.",
    "growth.reflection.question": "ما الفكرة التي غيّرت فهمك اليوم؟",
    "growth.reflection.placeholder": "شارك ما لفت انتباهك...",
    "growth.reflection.moodQuestion": "كيف تشعر الآن؟",
    "growth.reflection.mood.confident": "واثق",
    "growth.reflection.mood.motivated": "متحفّز",
    "growth.reflection.mood.proud": "فخور",
    "growth.reflection.mood.curious": "فضولي",
    "growth.reflection.mood.unsure": "غير متأكد",
    "growth.reflection.growthQuestion": "كم تطوّرت مقارنة ببداية هذا المستوى؟",
    "growth.reflection.growthLow": "قليلاً",
    "growth.reflection.growthHigh": "بشكل كبير",
    "growth.reflection.optional": "اختياري",
    "growth.reflection.submit": "حفظ والمتابعة",

    // ==================== GROWTH EVOLUTION (Phase 2) ====================
    "growth.evolution.title": "تطوّرك الشخصي",
    "growth.evolution.subtitle": "نظرة خاصة على المسار الذي قطعته",
    "growth.evolution.contrast": "مقارنة التأملات",
    "growth.evolution.then": "سابقاً",
    "growth.evolution.now": "الآن",
    "growth.evolution.moodTimeline": "تطوّر المزاج",
    "growth.evolution.growthCurve": "منحنى النمو الذاتي",

    // ==================== COMMITMENT INDEX ====================
    "commitment.title": "مؤشر الموثوقية",
    "commitment.subtext": "الاستمرارية تبني الثقة المهنية.",
    "commitment.message.high": "موثوقيتك قوية بشكل استثنائي",
    "commitment.message.medium": "أنت تبني الثقة خطوة بخطوة",
    "commitment.message.low": "استمر، كل خطوة تُحسب",
    "commitment.message.start": "ابدأ رحلتك نحو الاستمرارية",

    // ==================== PROMPTS PAGE ====================
    "prompts.badge": "مكتبة أوامر الذكاء الاصطناعي",
    "prompts.title": "اكتشف أوامر",
    "prompts.titleHighlight": "ذكاء اصطناعي قوية",
    "prompts.subtitle": "تصفح أوامر مختارة لنماذج الذكاء الاصطناعي للنصوص والصور والفيديو والصوت. انسخ، صوّت، ونفّذ فوراً.",
    "prompts.search": "بحث",
    "prompts.searchPlaceholder": "ابحث عن أوامر...",
    "prompts.promptType": "نوع الأمر",
    "prompts.category": "الفئة",
    "prompts.allCategories": "جميع الفئات",
    "prompts.sortBy": "ترتيب حسب",
    "prompts.resetFilters": "إعادة تعيين الفلاتر",
    "prompts.loading": "جارٍ التحميل...",
    "prompts.found": "{count} أمر(أوامر) موجودة",
    "prompts.noResults": "لم يتم العثور على أوامر",
    "prompts.noResultsHint": "حاول تعديل الفلاتر",
    "prompts.trending": "رائج",
    "prompts.view": "عرض",
    "prompts.copy": "نسخ",
    "prompts.copied": "تم نسخ الأمر إلى الحافظة!",
    "prompts.by": "بواسطة",
    "prompts.all": "الكل",
    "prompts.type.text": "نص",
    "prompts.type.image": "صورة",
    "prompts.type.video": "فيديو",
    "prompts.type.audio": "صوت",
    "prompts.cat.marketing": "تسويق",
    "prompts.cat.automation": "أتمتة",
    "prompts.cat.web_development": "تطوير الويب",
    "prompts.cat.education": "تعليم",
    "prompts.cat.data_science": "علم البيانات",
    "prompts.cat.business": "أعمال",
    "prompts.cat.ai_agents": "وكلاء ذكاء اصطناعي",
    "prompts.cat.other": "أخرى",
    "prompts.sort.trending": "🔥 رائج",
    "prompts.sort.popular": "👍 الأكثر شعبية",
    "prompts.sort.copies": "📋 الأكثر نسخاً",
    "prompts.sort.recent": "🕐 الأحدث",
    "prompts.requireAuth": "يجب تسجيل الدخول لاستخدام هذا الأمر.",
    "prompts.signIn": "تسجيل الدخول",

    // ==================== PROMPT DETAIL PAGE ====================
    "prompts.detail.notFound": "الأمر غير موجود",
    "prompts.detail.notFoundDesc": "هذا الأمر غير موجود أو تم حذفه.",
    "prompts.detail.browseAll": "تصفح جميع الأوامر",
    "prompts.detail.backToLibrary": "العودة إلى المكتبة",
    "prompts.detail.upvotes": "التصويتات",
    "prompts.detail.copies": "النسخ",
    "prompts.detail.views": "المشاهدات",
    "prompts.detail.upvote": "تصويت",
    "prompts.detail.upvoted": "تم التصويت",
    "prompts.detail.execute": "تنفيذ",
    "prompts.detail.content": "محتوى الأمر",
    "prompts.detail.readyTitle": "مستعد لاستخدام هذا الأمر؟",
    "prompts.detail.readyDesc": "انسخه إلى الحافظة أو نفّذه مباشرة في محادثة ذكاء اصطناعي.",
    "prompts.detail.copyPrompt": "نسخ الأمر",
    "prompts.detail.executePrompt": "تنفيذ الأمر",
    "prompts.detail.copiedAndOpening": "تم نسخ الأمر وفتح محادثة AI!",
  },
};

const LANGUAGE_STORAGE_KEY = "synra-language";

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && ["en", "fr", "ar"].includes(stored)) {
        return stored as Language;
      }
    }
    return "en";
  });

  const isRTL = language === "ar";
  const dir = isRTL ? "rtl" : "ltr";

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      let text = translations[language][key] || key;
      
      // Replace parameters like {name}, {year}
      if (params) {
        Object.entries(params).forEach(([paramKey, value]) => {
          text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), value);
        });
      }
      
      return text;
    },
    [language]
  );

  // Update document direction and lang attribute
  useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", language);
    document.body.classList.toggle("rtl", isRTL);
    
    // Add a class for RTL-specific styles
    if (isRTL) {
      document.documentElement.classList.add("rtl");
    } else {
      document.documentElement.classList.remove("rtl");
    }
  }, [language, dir, isRTL]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
